/**
 * care-model - shared frontend types + display helpers, mirrored from the API contract
 * (app/api/src/lib/event-types.ts and the care/events.ts list projection). One source of truth
 * for every view so the ClariCommand console renders the rev3 two-flow event model consistently.
 *
 * NO em dashes (William voice rule). ASCII hyphens only.
 */

// Base media channels + the three FOBT out-calling worklist channels (one per workbook tab, William
// 2026-07-09); mirrors api/src/lib/event-types.ts Channel.
export type Channel =
  | 'SMS' | 'Email' | 'Voice' | 'Referral' | 'Unknown'
  | 'Opt-in Not Returned' | 'Last-Yr Lapsed' | 'Screen + Not Returned';
export type Direction = 'inbound' | 'outbound';

/**
 * Where an event originated (rev4 queue coverage). 'member' = a direct member communication
 * (SMS / email / voicemail); 'intake' = a Lab Intake referral via the Portal CC fan-out;
 * 'print-station' = a Print Station referral (no_fax_number / fax_failed); 'portal' = any other
 * Portal-originated item; 'pcp' = a PCP-office-originated interaction (today: a live PCP-office call,
 * derived in the list projection from the caller:pcp-office tag - the resolution envelope is
 * unchanged). Absent on old rows = 'member' (the API projection defaults it).
 */
// Widened 2026-07-22 (mirrors api/src/lib/event-types.ts): three producer sources that 274 live
// events already carried outside the union - recording harvest + the two CS campaign batches.
// Widened 2026-09-05 (mirrors event-types.ts): 'fax-lane' = a stop on the fax rail awaiting an
// operator's decision, named for where it is worked (like 'out-calling'), not for who emitted it.
// Widened 2026-09-19 (mirrors event-types.ts, row 400 rev2): 'opt-in-referral' = an entry a Send to
// Main Dock minted in the main queue from the Opt In queue, named for where it is worked.
export type EventSource =
  | 'member' | 'intake' | 'print-station' | 'portal' | 'pcp' | 'out-calling'
  | 'recording-harvest' | 'cs-address-collection' | 'cs-kit-reconciliation'
  | 'fax-lane' | 'opt-in-referral' | 'bad-number'
  // Widened 2026-09-26 (mirrors event-types.ts, row 710): a member's email to ahp@, the HELP answer's address.
  | 'help-email';

/** Portal referral provenance carried on a referral-sourced event (rev4). */
export interface EventReferral {
  reason: string;
  recordId: string;
  sourceStage?: string;
  referredAt?: string;
  /** Fax-lane halt evidence (mirrors event-types.ts EventReferralHalt): both physicians as VALUES. */
  halt?: EventReferralHalt;
  /** ISO UTC, set once when the Portal resolved this referral upstream (see event-types.ts). */
  resolvedByPortal?: string;
  resolvedBy?: string;
}

export interface EventReferralHalt {
  previousSend: { faxNumber: string; pcpNpi: string | null; pcpName: string; sentTs: string };
  attemptedSend: { faxNumber: string; pcpNpi: string | null; pcpName: string };
  resultEvent: { stableId: string; kitBarcode: string; resultedTs: string };
  /** The Fax Station's job id and attempt (cdx-cc-referral/2); '' / 0 when the producer omitted them. */
  faxJobId: string;
  attempt: number;
}

/** The Portal's literal for a historical send whose practice cannot be reverse-resolved. */
export const PRACTICE_NOT_RESOLVED = 'practice not resolved';

/** Widened rev3 lifecycle plus the retained legacy values (responded/closed). */
export type EventStatus =
  | 'new'
  | 'in_progress'
  | 'actioned'
  | 'in_review'
  | 'cleared'
  | 'spam'
  | 'closed-no-action'
  | 'responded'
  | 'closed';

/** Matching v2 cascade provenance (mirrors api/src/lib/event-types.ts). */
export type MatchedBy =
  | 'phone1'
  | 'phone2'
  | 'email'
  | 'known-contact'
  | 'kit-barcode'
  | 'insurance-id'
  | 'member-id'
  | 'manual'
  | 'none';
export type MatchConfidence = 'exact' | 'ambiguous' | 'none';

/** Enriched candidate-chip context, parallel to matchCandidates (matching v2, the MA/HUANG rule). */
export interface MatchCandidateInfo {
  memberId: string;
  fullName: string;
  dob: string;
  lastActivity: string;
  /** True when the candidate set came from one shared phone key (household disambiguation). */
  sharedPhone?: boolean;
}

/** SMS compliance flag set at ingest: STOP-family -> 'opt-out', HELP -> 'help'. Suggestion only. */
export type SuggestedAction = 'opt-out' | 'help';
export type ConsentState = '' | 'on-file' | 'none' | 'unknown';
export type ResponseChannel = 'email-reply' | 'sms' | 'call-back' | 'none' | '';

export type ActionType =
  | 'change-of-address'
  | 'replacement-kit'
  | 'opt-out'
  | 'do-not-contact'
  // 2026-09-14: the class that keeps a member contactable. See the API's event-types.ts for why it
  // exists and why it stays internal.
  | 'decline'
  | 'opt-in'
  | 'result-letter-resend'
  | 'special-case'
  | 'consent-update'
  | 'callback-scheduled'
  | 'pcp-fax-resend'
  | 'test-completed'
  | 'general-inquiry'
  | 'referred-to-client-services'
  // Row 232: a first kit the member asked for (addendum /1.1). Internal until S5 accepts /1.1.
  | 'kit-request';

/**
 * Item 6 "Requests" vocabulary (William 2026-07-08): a LOG of what the member said / asked for on the
 * interaction. Distinct from ActionType (item 12 disposition, the operator's good-faith decision of
 * what to DO). A logged request never dictates a disposition (member asks for a kit -> logged here;
 * the operator may still decide "nothing / close matter" in item 12). Internal capture only - requests
 * do NOT ride the Portal resolution envelope. 'training-question' = the member only had questions,
 * answered live, nothing more to do.
 */
export type RequestType =
  | 'change-of-address'
  | 'replacement-kit'
  | 'opt-out'
  | 'opt-in'
  | 'do-not-contact'
  | 'decline'
  | 'general-inquiry'
  | 'referred-to-client-services'
  | 'test-completed'
  | 'result-letter-resend'
  | 'consent'
  | 'pcp-refax'
  | 'callback'
  | 'special-case'
  | 'training-question';

/** One captured request (what the member said/asked), stored on rec.requests[]. Internal only.
 * subtype (2026-07-14 feedback): the per-type dropdown refinement (test-completed method,
 * replacement-kit reason). */
export interface EventRequest {
  type: RequestType;
  subtype?: string;
  note?: string;
  ts: string;
  by?: string;
  byName?: string;
}

export interface StatusEvent {
  status: EventStatus;
  ts: string;
  by?: string;
  byName?: string;
}

export interface EventAction {
  type: ActionType;
  ts: string;
  by?: string;
  byName?: string;
  detail?: string;
  data?: Record<string, unknown>;
  handoffRef?: string;
}

/**
 * WAVE 3 ITEM 4 (William, 2 Oct 2026: "Both take effect once approved. and, all items go to approval
 * then take effect."): one press that WAITS FOR APPROVAL on a case, as the record carries it. Present
 * only with the deployment's MEMBERCARE_STAGED_PRESSES on; the press takes effect when the case is
 * approved (the approver is the actor, the presser is kept). Mirrors api/src/lib/event-types.ts.
 */
/** PART Q (William, 17 Oct 2026): the whole email of an Email case (api lib/email-raw.ts EmailView). */
export interface EmailView {
  fromName: string;
  fromAddress: string;
  to: string[];
  cc: string[];
  sent: string;
  sentLabel: 'Sent' | 'Received';
  subject: string;
  body: string;
  earlier: string;
  bodyWasHtml: boolean;
  attachments: string[];
  empty: boolean;
  source: 'raw' | 'record';
}

/** PART R: a suggestion as the list row carries it (no date of birth). */
export interface RowSuggestion {
  memberId: string;
  fullName: string;
  campaign: string | null;
  score: number;
  reasons: string[];
  otherCount: number;
  preTick: boolean;
}

/** PART P: one candidate of the suggestion stored on the case (api lib/event-types.ts MatchSuggestionCandidate). */
export interface MatchSuggestionCandidate {
  memberId: string;
  fullName: string;
  dob: string;
  campaign: string | null;
  score: number;
  reasons: string[];
}

/** PART P: the suggestion stored on the case (api lib/event-types.ts MatchSuggestion). */
export interface MatchSuggestion extends MatchSuggestionCandidate {
  at: string;
  preTick: boolean;
  others: MatchSuggestionCandidate[];
  otherCount: number;
  dismissed?: string[];
  cleared?: { by: string; at: string };
  displayName?: string;
}

export interface PendingPress {
  /** Unique on the entry: the press's clientRequestId, else minted on the server. */
  id: string;
  /** 'action' (L2, 16 Oct 2026): a drawer disposition that waits for approval; today only the opt back in. */
  kind: 'reply' | 'key' | 'close-file' | 'action' | 'match';
  /** reply: opt-in | opt-out | stop | refer; key: the door op; close-file: 'close-file'; action: 'opt-in'. */
  op: string;
  /** What the immediate path needs to act at approval; never more member words than it stores today. */
  body: Record<string, unknown>;
  /** Who pressed (address and name), and when (UTC ISO). */
  by: string;
  byName: string;
  at: string;
}

/**
 * WAVE 3 ITEM 4: what one entry's row answers to a reply press. `staged` = the press was QUEUED for
 * approval and nothing took effect yet; it is never read as done (lib/staged-presses.ts isStagedRow).
 */
export type ReplyActionOutcome = 'done' | 'refused' | 'error' | 'staged';

/** One of Marny's outbound touches (call / email / SMS she made herself and logged). rev4. */
export interface ContactEntry {
  channel: 'call' | 'email' | 'sms';
  ts: string;
  by?: string;
  byName?: string;
  note: string;
  recordingRef?: string;
}

/** One file appended to the event (Teams recording, consent email, image, document). rev4. */
export interface EventAttachment {
  id: string;
  ts: string;
  by?: string;
  byName?: string;
  kind: 'recording' | 'consent' | 'image' | 'document';
  name: string;
  ref: string;
  size: number;
}

/** A light member/PCP tag added by the operator (rev4). */
export interface EventTag {
  tag: string;
  entity: 'member' | 'pcp';
  ts: string;
  by?: string;
}

export interface HandoffRef {
  type: string;
  ts: string;
  ref: string;
  status?: string;
}

export interface MemberSnapshot {
  member_id: string;
  insurance_id: string;
  full_name: string;
  dob: string;
  language: string;
  phone1: string;
  phone2: string;
  email: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  mso: string;
  ipa: string;
  lob: string;
  market: string;
  cohort: string;
  kit_stage: string;
  pcp_name: string;
  pcp_phone: string;
  pcp_fax: string;
  pcp_npi?: string; // rev4 (Portal contract 4b.1); absent on older sidecars
  last_activity: string;
}

/** PHI-light list row returned by GET /api/care/events (see care/events.ts toRow). */
export interface EventRow {
  eventId: string;
  /** The plan this row belongs to. Always present; every event carries it. */
  client: string;
  /**
   * The campaign this interaction belongs to, or null when there is none to know. NULL IS A REAL
   * STATE and the common one for member-initiated contact, never a missing value to be filled in
   * later. See `EventRecord.campaign` (API) for the three provenance groups and why it is never
   * derived from the member.
   */
  campaign: string | null;
  /** How the campaign came to be set (row 217(b)); null when nothing chose one. Mirrors the API. */
  campaignSource?: CampaignSource | null;
  /** Slice B: the round of the campaign a text reply answered ("Opt-in 02"); null when not a reply. */
  cycle?: string | null;
  receivedTs: string;
  channel: Channel;
  direction: Direction;
  mode: string;
  fromPhone: string;
  /** Derived from the internal-numbers roster (feedback #17): CDX staff/test traffic, badged. */
  internal?: boolean;
  fromAddress: string;
  subject: string;
  preview: string;
  hasAudio: boolean;
  hasTranscript: boolean;
  memberId: string;
  memberName: string;
  /** Slice B: where the name came from: the entry's own snapshot, or its campaign's roster ('' = none). */
  memberNameFrom?: 'file' | 'roster' | '';
  matchedBy: MatchedBy;
  matchConfidence: MatchConfidence;
  status: EventStatus;
  followUp: boolean;
  hasNotes: boolean;
  actionCount: number;
  /** The one-word action this row carries (latest typed action; '' if none). Rendered as the Instruction column. */
  instruction: string;
  /** Distinct business action (disposition) types on this row, for the Final Review pill column (item 12). */
  actions: string[];
  handoffCount: number;
  /**
   * Wave 3 item 4: how many presses wait for approval on this case (0 when none). Absent on a server
   * before that build, which reads as 0 (lib/staged-presses.ts pendingCountOf). Mirrors care/events.ts toRow.
   */
  pendingCount?: number;
  /**
   * PART R (William, 17 Oct 2026): who an unmatched entry is probably from, with the percent and the reasons
   * (care/events.ts listSuggestion). Absent or null when the entry has a member, the pass found nobody, or an older
   * server sent none.
   */
  suggestion?: RowSuggestion | null;
  /** PART S2: the Find Member match waiting on the case (care/events.ts listMatchPending); null when none waits. */
  matchPending?: { memberId: string; memberName: string; decision: string } | null;
  /** PART N: a stop waits for approval on this case, and since when (ISO). */
  stopPending?: boolean;
  stopSince?: string;
  assignedTo: string;
  operatorName: string;
  operatorEmail: string;
  clearedTs: string;
  updatedAt: string;
  // rev4 projection additions (see care/events.ts toRow)
  source: EventSource;
  referralReason: string;
  sentToReviewTs: string;
  attachmentCount: number;
  contactCount: number;
  /** Slice C (img10): the newest call on the entry (api/src/lib/quick-keys.ts lastCallOf); absent from an older API. */
  lastCall?: { at: string; outcomes: string[]; reached: boolean | null } | null;
  /** Resolution TAT in whole minutes, ONLY once sentToReviewTs is set; null = still open (tick live). */
  tatMinutes: number | null;
  /** True when Claire finished intake and auto-routed this event to review (the "Claire Auto Resolved" pill). */
  claireResolved?: boolean;
  /** Row 232: the platform's class of a text reply pulled from ClariConnect's feed; '' on every other event. */
  replyClass?: SmsReplyClass | '';
  /** Slice B: the platform's id of this reply and the intent it read ('' when not a reply). */
  replyMessageId?: string;
  replyIntent?: string;
  /**
   * William's auto sort (2 Oct 2026): the distinct TYPES of the asks recorded in the case's Requests
   * (item 6; Claire records one at intake, the operator the rest), codes only, never the note. The 01A
   * page's sorted folders read them (lib/sort-folders.ts). Absent on a server before that build.
   * Mirrors api/src/care/events.ts toRow.
   */
  requestTypes?: RequestType[];
  /**
   * Approval 185: the offer a text reply answered (KIT_KIND_BY_TEMPLATE of its templateKey; '' for an
   * unknown key and on every other event). Mirrors api/src/care/events.ts replyOfferOf.
   */
  replyOffer?: ReplyOffer | '';
  /**
   * Approval 185: the member's cohort in the roster's OWN words ("Cohort 4 - KED"), on a text reply
   * only; '' otherwise. The cohort decides the kit, and the mapping is not confirmed, so the screen
   * shows these words and never a kit name.
   */
  replyCohort?: string;
  /**
   * Row 400: the KIT the cohort maps to in the published cohort-to-kit map, read on the server for the
   * reply's campaign; '' when the map has not loaded, has no entry, or the row is not a text reply.
   * Never invented on either side: a blank shows the cohort's words marked pending.
   */
  replyKit?: string;
  // Thread grouping + suggested action (William 2026-07-09). Mirrors api/src/care/events.ts toRow.
  /** The CALLER's PCP NPI when a live call recorded one (from the caller:pcp-office tag); '' otherwise. */
  pcpNpi: string;
  /** What this message appears to ASK FOR. A suggestion for the operator, never an action taken. */
  suggestedAction: SuggestedActionKey | '';
  /** The phrase or signal that produced the suggestion; shown as the cell tooltip. */
  suggestedActionReason: string;
  /** 'high' = parsed signal (SMS STOP/HELP, Claire). 'low' = a text rule, so treat it as a hint. */
  suggestedActionConfidence: 'high' | 'low' | '';
  /**
   * Derived (never stored): this row's STOP was reversed by a later START from the same number, so
   * the SMS platform has already re-subscribed the member. Drives the RE-SUBSCRIBED pill and the
   * drawer's block on the opt-out dispositions. See api/src/lib/resubscribe.ts.
   */
  resubscribedAfterStop?: boolean;
  /** receivedTs of that START; '' whenever the flag is false. */
  resubscribedStartTs?: string;
  /**
   * Derived (never stored): the matched member is CURRENTLY marked do_not_solicit in 26fobt.
   * Drives the Calling page's live call-button guard - the worklist excluded DNS members at load
   * time, but a member marked after the load must not keep a hot call button.
   */
  doNotSolicit?: boolean;
  /**
   * The PERMANENT OPT-OUT RECORD says this member is suppressed (William 14 Sep 2026). Distinct from
   * `doNotSolicit` above, which is the PLAN's own flag: one is what this plan enforces, the other is
   * what every station together has recorded, and they disagree often enough that collapsing them
   * would hide the disagreement the review screen exists to surface. Drives the grey shade.
   */
  optedOut?: boolean;
  /**
   * APPROVAL 187, BLOCKING: the member refused AFTER this reply (a STOP no START lifted, a 2 no later
   * 1 took back), or the phone is on the platform's do-not-text list. Null when nothing refuses, and
   * always null with the reply buttons off. Mirrors api/src/lib/reply-history.ts LaterRefusal.
   */
  laterRefusal?: LaterRefusalFlag | null;
  /** APPROVAL 187 (d), SOFT and never blocking: the member said 2, then 1 again. Call first. */
  answerChanged?: AnswerChangedFlag | null;
  /**
   * APPROVAL 188, all read on the server from the member's reply history (api/src/lib/reply-history.ts)
   * and all empty with the reply buttons off. The answer this reply gives ('' for none; a reaction
   * never answers), the group it is shown in (the member's LAST answer per campaign is the Opt In or
   * Opt Out row, every other reply of theirs is All other), why it sits outside its answer's group,
   * and whether Select all may take it ('' yes; otherwise read first, tickable one by one).
   */
  replyAnswer?: 'yes' | 'no' | '';
  /**
   * ROW 400 REV2, his Indication: 'actual' when the reply's firm word is exactly "1", 'implied' for
   * any other yes ("yes!!", "one", "ok", the platform's yes class on a sentence), '' when the reply is
   * not a yes. Read on the server (api/src/lib/reply-history.ts indicationOf), one rule.
   */
  replyIndication?: 'actual' | 'implied' | '';
  replyGroupHint?: 'optin' | 'optout' | 'help' | 'other' | '';
  replyNote?: ReplyNoteFlag | null;
  replyReadFirst?: '' | 'soft' | 'said-more-after';
  /**
   * ROW 419: the mark this entry already carries (its own decline or do-not-contact action): the kind,
   * when, by whom, and (an opt-out) which campaign. Null when it carries none, and absent on a server
   * before row 419. The Opt Out and STOP sub-tabs show it instead of a tick box. Mirrors
   * api/src/lib/care-mark.ts EntryMark.
   */
  replyMark?: ReplyMarkFlag | null;
  /**
   * William, 22 Oct 2026: the names of the presses waiting on the case ("Opt-Out", "Stop", "Send results"), in
   * the order pressed, so Final Review's Actions column says what approval will do. Absent on a server before
   * this build, which reads as none (lib/staged-presses.ts pendingOpsOf). Mirrors care/events.ts toRow.
   */
  pendingOps?: string[];
}

/** ROW 419: a mark on an entry, as the list row carries it (api/src/lib/care-mark.ts EntryMark). */
export interface ReplyMarkFlag {
  /** 'optout', or the stop kind (MARK_STOP_KIND in lib/opt-in-lane.ts, pinned against the API's one constant). */
  kind: string;
  at: string;
  by: string;
  byName: string;
  campaign: string | null;
  /** True when the action carries the row 419 mark object (a press since that build). */
  hasMark: boolean;
}

/**
 * ROW 419: one row of the builder's care_marks table as POST /api/care/member-comms and
 * GET /api/care/care-marks answer it (api/src/lib/care-db.ts CareMarkDbRow): a mark an entry carries,
 * from a Care action (`source: 'care'`) or the platform's own reading of the reply (`source: 'platform'`).
 */
export interface CareMarkRow {
  client: string;
  eventId: string;
  memberId: string;
  memberName: string;
  kind: string;
  campaign: string | null;
  /** The member's own words, 500 characters at most. */
  sourceText: string;
  markedAt: string;
  markedBy: string;
  markedByName: string;
  statusAtBuild: EventStatus | string;
  archived: boolean;
  source: 'care' | 'platform';
  program: string | null;
  year: number | null;
  basis: string | null;
  evidence: string | null;
  locked: boolean | null;
}

export interface CareMarksResp {
  client: string;
  kind: string;
  campaign: string | null;
  available: boolean;
  /** True on a database built before the table: the screen says "not there yet", never "none". */
  tableAbsent: boolean;
  builtAt: string;
  error?: string;
  count: number;
  total: number;
  rows: CareMarkRow[];
}

/**
 * ROW 400 REV2: one archived communication of a member, from member_care.db through
 * POST /api/care/member-comms (api/src/care/member-comms.ts toCommRow). PHI-light like the queue row.
 */
export interface MemberCommRow {
  eventId: string;
  memberId: string;
  receivedTs: string;
  channel: Channel;
  source: EventSource | string;
  status: EventStatus;
  subject: string;
  preview: string;
  campaign: string | null;
  hasAudio: boolean;
  replyClass: SmsReplyClass | '';
  clearedTs: string;
  resolution: string;
}

export interface MemberCommsResp {
  client: string;
  members: number;
  /** False when member_care.db could not be read: the screen says "history not read", never "none". */
  available: boolean;
  /** When the builder wrote the database (ISO); '' when unavailable. Up to ten minutes behind. */
  builtAt: string;
  error?: string;
  count: number;
  rows: MemberCommRow[];
  /** Row 419: false when the database predates the care_marks table (the history still answers). */
  marksAvailable?: boolean;
  /** Row 419: the named members' marks, newest first; absent on a server before row 419. */
  marks?: CareMarkRow[];
}

/** APPROVAL 188: `eventId` / `at` name the member's last answer; '' only on a reaction from a member who answered nothing. */
export interface ReplyNoteFlag {
  kind: 'earlier-answer' | 'answer-elsewhere' | 'reaction';
  eventId: string;
  at: string;
}

export interface LaterRefusalFlag {
  reason: 'later_stop' | 'later_decline' | 'do_not_text';
  /** The later reply; '' for do_not_text. */
  eventId: string;
  /** That reply's receivedTs; '' for do_not_text. */
  at: string;
  replyClass: SmsReplyClass | '';
}

export interface AnswerChangedFlag {
  declinedEventId: string;
  declinedAt: string;
  yesEventId: string;
  yesAt: string;
}

/**
 * What a message appears to be asking for (api/src/lib/intent.ts). Distinct from `SuggestedAction`
 * above, which is the record's SMS-compliance flag ('opt-out' | 'help') written by the poller. Eight
 * of these nine keys are literal ActionType values, so a suggestion reads with the same word the
 * operator will click in the drawer.
 */
export type SuggestedActionKey =
  | 'replacement-kit'
  | 'result-letter-resend'
  | 'change-of-address'
  | 'opt-out'
  | 'consent-update'
  | 'callback-scheduled'
  | 'pcp-fax-resend'
  | 'test-completed'
  | 'help-reply';

/** How a thread was keyed. Precedence order: member, then PCP, then phone, then email. */
export type ThreadKind = 'member' | 'pcp' | 'phone' | 'email' | 'event';

/**
 * One consolidated row: every event for a member / PCP / contact (api/src/lib/thread-model.ts).
 * A thread is a PROJECTION. `eventId` is still the unit of work; opening a thread opens its events.
 */
export interface ThreadRow {
  threadKey: string;
  kind: ThreadKind;
  label: string;
  sublabel: string;
  memberId: string;
  memberName: string;
  matchConfidence: MatchConfidence;
  pcpNpi: string;
  fromPhone: string;
  fromAddress: string;
  /**
   * 16 Oct 2026 (the Contact column): the newest sender address in the thread that is a person's, never
   * a relay's. Optional so an older server still fits; absent or '' when the thread holds none.
   */
  contactEmail?: string;
  /** Every origin the thread holds, newest event first - not just its newest event's. */
  sources: EventSource[];
  /** Every campaign this thread holds; empty means no campaign-bearing event. Mirrors the API. */
  campaigns: string[];
  channels: Channel[];
  /** Slice B (his img7): the plan, the cycles held, the cohort, the offers answered, the newest reply's class. Optional so an older server still fits. */
  client?: string;
  cycles?: string[];
  cohort?: string;
  offers?: string[];
  lastReplyClass?: string;
  eventCount: number;
  openCount: number;
  newCount: number;
  firstTs: string;
  lastTs: string;
  status: EventStatus;
  /** New activity arrived on a thread someone already worked. Drives the row highlight. */
  hasNewActivity: boolean;
  suggestedAction: SuggestedActionKey | '';
  suggestedActionReason: string;
  suggestedActionConfidence: 'high' | 'low' | '';
  /** The thread HOLDS a STOP a later START reversed (aggregated, never just the newest event: the
   * newest event on a re-subscribe is always the START). Drives the grouped view's pill. */
  resubscribedAfterStop?: boolean;
  /** receivedTs of the START that reversed the newest such STOP; '' when the flag is false. */
  resubscribedStartTs?: string;
  hasAudio: boolean;
  hasNotes: boolean;
  followUp: boolean;
  assignedTo: string;
  operatorName: string;
  tatMinutes: number | null;
  /** receivedTs of the oldest OPEN event; live-tick the TAT from here while tatMinutes is null. */
  oldestOpenTs: string;
  latestEventId: string;
  eventIds: string[];
}

export interface SourceTotals {
  member: number;
  intake: number;
  printStation: number;
  portal: number;
  pcp: number;
  outCalling: number;
}

export interface Totals {
  new: number;
  in_progress: number;
  actioned: number;
  in_review: number;
  cleared: number;
  responded: number;
  closed: number;
  'closed-no-action': number;
  spam: number;
  unmatched: number;
  bySource?: SourceTotals;
}

export interface ListResp {
  client: string;
  count: number;
  totals?: Totals;
  items: EventRow[];
  /** Print Station fax-failure events in scope, counted whether or not they were excluded. */
  faxFailures?: number;
  /** Present only when the request passed groupBy=thread. `items` is unaffected by grouping. */
  threads?: ThreadRow[];
  /** Same shape as `totals`, but counting THREADS by rollup status. Drives the tabs in thread mode. */
  threadTotals?: Totals;
  /**
   * APPROVAL 187: present only with the reply buttons on. False = the platform's do-not-text list was
   * NOT read, so no row can say do_not_text; the screen says "do-not-text list not checked".
   */
  doNotTextChecked?: boolean;
  /**
   * ROW 400: present only with the reply buttons on. Whether the cohort-to-kit map is in force on the
   * server (and how many cohorts it names), so a blank Kit cell can be told from a map not loaded.
   */
  cohortKit?: { loaded: boolean; entries: number; error: string | null };
}

/** Full record returned by GET /api/care/event (raw EventRecord, with the PHI snapshot). */
/**
 * How an event's campaign came to be set (row 217(b), William's "Latest Start Date" of 16 Sep 2026):
 * the referral or worklist said so, the poller derived the member's newest campaign by launch date,
 * or Marny chose it in the drawer. The wording is in ONE table so a change of words touches nothing.
 */
export type CampaignSource = 'source' | 'derived-newest' | 'operator' | 'reply' | 'offer';
export const CAMPAIGN_SOURCE_WORDING: Record<CampaignSource, { label: string; help: string }> = {
  source: { label: 'from the referral', help: 'The Portal named this campaign on the referral or the worklist.' },
  'derived-newest': { label: 'most recent campaign', help: "Chosen automatically: the member's campaign with the latest launch date. You can change it." },
  operator: { label: 'chosen by an operator', help: 'An operator set this campaign in the drawer.' },
  reply: { label: 'from the text answered', help: 'The platform tied this reply to the campaign of the text it answered (its cycle is shown beside it).' },
  // Row 718 (William, 26 Sep 2026): mirrors api/src/lib/event-types.ts campaignSource.
  offer: { label: 'from the offer sent', help: 'The entry named no campaign, so the Opt In press took it from the offer text the member was sent.' },
};

/** Row 232: a text reply pulled from ClariConnect's feed (mirrors api/src/lib/event-types.ts). */
export type SmsReplyClass = 'YES_KIT' | 'DECLINE_CAMPAIGN' | 'STOP' | 'HELP' | 'OTHER';
export interface SmsReplyBlock {
  messageId: string;
  classification: SmsReplyClass;
  intent: string | null;
  keyword: string | null;
  templateKey: string | null;
  campaignId: string | null;
  campaignName: string | null;
  language: string | null;
  memberExternalId: string | null;
  shipTo?: {
    line1: string; line2: string; city: string; state: string; zip: string;
    mailOutcome?: string; uspsResult?: string; uspsDeliveryFlags?: string;
    usps?: { line1: string; line2: string; city: string; state: string; zip: string; zip4: string };
    source?: string;
  };
}
export type SmsYesShipPath = 'care' | 'portal' | 'auto' | 'undecided';

/**
 * The reply classes in words, in ONE table. The pill, the chip and the drawer all read it. A decline
 * says in its own words that it is not an opt-out, because that is the misreading that costs a member.
 */
export const REPLY_CLASS_META: Record<SmsReplyClass, { label: string; pill: string; help: string }> = {
  YES_KIT: { label: 'Kit request (texted 1)', pill: 'pill-success', help: 'The member texted 1 (yes) to a kit offer. A kit to ship once the address is confirmed.' },
  DECLINE_CAMPAIGN: { label: 'Declined this campaign', pill: 'pill-accent', help: 'The member texted 2 (no) to THIS campaign only. Not an opt-out and not a STOP: they stay contactable for everything else.' },
  STOP: { label: 'STOP', pill: 'pill-danger', help: 'The member texted STOP: an opt-out from all communications.' },
  HELP: { label: 'HELP', pill: 'pill-warning', help: 'The member texted HELP. The platform sent its help reply; answer anything further here.' },
  OTHER: { label: 'Question', pill: 'pill-primary', help: 'Anything else the member texted: a question or issue to handle.' },
};

/**
 * Row 269 is William's: how a texted yes ships. Both labels exist behind MEMBERCARE_SMS_YES_SHIP_PATH
 * so his word flips a setting, not a build. The server refuses a Care ship on any other path.
 */
export const SMS_YES_SHIP_WORDING: Record<SmsYesShipPath, { button: string; note: string }> = {
  care: {
    button: 'Confirm address and request kit',
    note: 'Confirming records a kit request with this address. It ships once the Portal accepts kit requests and an A1c-KED kit rail exists; until then it is held (row 270).',
  },
  portal: {
    button: 'Address confirmed',
    note: 'The Portal ships texted-yes kits on its own. Confirming records that you checked the address; it never ships a second kit.',
  },
  auto: {
    button: 'Address confirmed',
    note: 'Requested automatically when the text arrived (row 269). The Portal ships to the roster address after the delivery block check. Confirming records that you checked the address; it never ships a second kit.',
  },
  undecided: {
    button: 'Ship path not decided',
    note: 'How a texted yes ships is waiting on a decision (row 269). Nothing can ship from here yet.',
  },
};
export const SMS_OPTIN_YES_REASON = 'sms-optin-yes';
/** Row 269 'auto': did the platform tie this reply to one member (so the agent already requested the kit)? */
export function smsReplyTied(reply: { memberExternalId?: string | null } | null | undefined): boolean {
  return !!String(reply?.memberExternalId ?? '').replace(/^0+/, '').trim();
}
/**
 * The path a click on this reply actually takes. On 'auto' a tied reply only confirms (the agent
 * requested it); an untied one is the hand path, which is Care's own confirm-and-request.
 */
export function effectiveShipPath(path: SmsYesShipPath, reply: { memberExternalId?: string | null } | null | undefined): SmsYesShipPath {
  if (path !== 'auto') return path;
  return smsReplyTied(reply) ? 'auto' : 'care';
}
/** The offer a text reply answered, as its kit kind (addendum /1.1, section 3). */
export type ReplyOffer = 'a1c' | 'ked' | 'a1cked';
export const KIT_KIND_BY_TEMPLATE: Record<string, ReplyOffer> = { a1c_optin: 'a1c', ked_optin: 'ked', a1cked_optin: 'a1cked' };

/** One email reply as the entry records it (api/src/lib/event-types.ts EmailReplyRecord). */
export interface EmailReplyRow {
  clientRequestId: string;
  status: 'pending' | 'sent' | 'not-sent' | 'unknown';
  at: string;
  sentAt?: string;
  by: string;
  byName?: string;
  mailbox: string;
  to: string;
  subject?: string;
  text: string;
  foundBy?: string;
  reason?: string;
}

export interface EventRecord {
  eventId: string;
  client: string;
  campaign?: string | null;
  campaignSource?: CampaignSource | null;
  campaignBasis?: string;
  /** The current-campaign rosters that carried the matched phone (23 Sep 2026); absent otherwise. */
  matchRosters?: string[];
  graphMessageId?: string;
  receivedTs: string;
  channel: Channel;
  direction: Direction;
  fromAddress: string;
  fromPhone: string;
  /** Derived (never stored): the sender is on the internal-numbers roster (feedback #17). */
  internal?: boolean;
  /** Derived (never stored): a later START from this number already reversed this STOP, so the SMS
   * platform has re-subscribed the member. See api/src/lib/resubscribe.ts. */
  resubscribedAfterStop?: boolean;
  /** receivedTs of that START; '' whenever the flag is false. */
  resubscribedStartTs?: string;
  phoneSource?: string;
  subject: string;
  content: string;
  hasAudio: boolean;
  audioRef: string;
  rawRef?: string;
  memberId: string;
  matchedBy: MatchedBy;
  matchConfidence: MatchConfidence;
  memberSnapshot: MemberSnapshot | null;
  /** Slice B: where the snapshot came from on the row-open read: the file, or the entry's campaign roster ('' = none). */
  memberSnapshotFrom?: 'file' | 'roster' | '';
  matchCandidates: string[];
  matchCandidateInfo?: MatchCandidateInfo[];
  status: EventStatus;
  assignedTo: string;
  responseText: string;
  responseTs: string;
  responseChannel: ResponseChannel;
  notes: string;
  /** William feedback #1 (2026-07-14): notes are internal-only; this is the deliberate note FOR the
   * Portal - it alone rides the resolution envelope's notes field at approval. */
  portalNote?: string;
  followUp: boolean;
  mode?: string;
  operatorName?: string;
  operatorEmail?: string;
  statusHistory?: StatusEvent[];
  actions?: EventAction[];
  /** Wave 3 item 4: the presses waiting for approval on this case, oldest first; absent when none ever queued. */
  pending?: PendingPress[];
  /** PART P (17 Oct 2026): who this unmatched entry is probably from; never a match. */
  matchSuggestion?: MatchSuggestion;
  /** PART N: the stop blocks Care put on provisionally (api lib/stop-approval.ts). */
  provisionalBlocks?: Array<{ id: string; kind: string; source: string; verb: string; status: string; liftHow: string; at: string; final?: { at: string; by: string; careLine?: string }; lifted?: { at: string; by: string; reason: string; outcome: string };
    /** T-1025-CARE-08: a lift Care tried that did not happen (the block stays open), in Care's fixed words. */
    liftFailed?: { at: string; by: string; reason: string; outcome: string; attempts?: number };
    /** CARE-08 second review S3: the print, mail and kits hold this block carries (api lib/event-types.ts ProvisionalCareHold). */
    careHold?: { memberId?: string; scope?: string; campaign?: string | null; stopLine?: string } }>;
  clearedTs?: string;
  reviewedBy?: string;
  consentState?: ConsentState;
  transcriptRef?: string;
  interactionStartTs?: string;
  interactionEndTs?: string;
  captureCreatedTs?: string;
  timestampSource?: string;
  teamsCallId?: string;
  recordingDurationSec?: number;
  handoffs?: HandoffRef[];
  // rev4 Customer Care fields (all optional, mirrored from api/src/lib/event-types.ts)
  source?: EventSource;
  referral?: EventReferral;
  /** Row 232: present only on a reply pulled from ClariConnect's feed. */
  /** M1 part B: every email reply attempted from this entry (the API's EmailReplyRecord), oldest first. */
  emailReplies?: EmailReplyRow[];
  smsReply?: SmsReplyBlock;
  /** Row 578b (mirrors event-types.ts): set on a 'bad-number' entry. */
  smsRejection?: { messageId: string; status: string; errorCode: string; toPhone: string; campaignId: string; campaignName: string; createdTs: string; memberExternalId: string };
  /** Row 269: the deployment's ship path for a texted yes (a projection from GET /api/care/event, never stored). */
  smsYesShipPath?: SmsYesShipPath;
  /**
   * R. 82 / row 401: which folder GET /api/care/event answered from. True = the archive agent has
   * moved this closed entry out of events/, so the drawer renders it READ-ONLY. A projection, never
   * stored; absent on a record read from the queue's live folder.
   */
  archived?: boolean;
  contactLog?: ContactEntry[];
  attachments?: EventAttachment[];
  tags?: EventTag[];
  /** Item 6 (William 2026-07-08): what the member said/asked (internal capture, not in the envelope). */
  requests?: EventRequest[];
  sentToReviewTs?: string;
  approvedTs?: string;
  resolution?: string;
  reviewBatchId?: string;
  pcpFaxCorrected?: string;
  suggestedAction?: SuggestedAction;
  // Claire receptionist ingest (claire-interaction/1); mirrors api/src/lib/event-types.ts. resolved
  // auto-routes to review with the claireResolved pill; sms_consent is a per-interaction opt-in.
  claireResolved?: boolean;
  claireInteractionId?: string;
  claireRequestType?: string;
  callerName?: string; // PHI (record-only)
  callerDob?: string; // PHI (record-only)
  smsConsent?: boolean;
  smsPreferred?: boolean;
  // rev7 unified voice+SMS thread slice (fleet contract 2026-07-11); mirrors api event-types.ts.
  claireLanguage?: string;
  claireSummary?: string;
  claireReason?: string;
  claireTranslatedTranscript?: string;
  claireTranslatedReason?: string;
  /** Live-call mint idempotency key (call-start); absent on every other event kind. */
  clientRequestId?: string;
  createdAt?: string;
  updatedAt: string;
  auditPath?: string;
}

/* ------------------- rev4 member lookup (search + reveal) ------------------- */

/** PHI-light typeahead candidate (GET /api/care/member-search). */
export interface MemberCandidate {
  member_id: string;
  insurance_id: string;
  full_name: string;
  dob: string;
  language: string;
  kit_stage: string;
  pcp_name: string;
  phone1: string;
  /** The current-campaign search (23 Sep 2026): the listed rosters this member is on. Absent otherwise. */
  campaigns?: string[];
  /** PART A (Find Member): city and state, when the roster carries them. */
  city?: string;
  state?: string;
}

export interface MemberSearchResp {
  q: string;
  count: number;
  candidates: MemberCandidate[];
  note?: string;
  /** PART F: why the list is empty: 'source-unreachable' (the roster could not be read) or 'no-rows'. */
  emptyReason?: string;
  /** PART F: the server's reason the roster could not be read. */
  unreadReason?: string;
  /** R. 81: the campaign roster the search read (absent on a response from an older API). */
  campaign?: string | null;
}

/** Reason-gated full-record reveal (POST /api/care/member-detail). Satellites are fail-soft. */
export interface MemberDetailResp {
  found: boolean;
  memberId: string;
  /** R. 81: the campaign roster this record was read from (absent on a response from an older API). */
  campaign?: string | null;
  member: Record<string, unknown> | null;
  events: Record<string, unknown>[];
  kitTracking: Record<string, unknown>[];
  results: Record<string, unknown>[];
  faxTransmissions: Record<string, unknown>[];
  resultLetters: Record<string, unknown>[];
  /**
   * What the client's program OFFERS (capability gating, registry half, 2026-09-06): a `device`
   * program has no results, letters or result faxes, and the panel says so instead of "none".
   * Absent on a response from an older API, which reads as everything offered.
   */
  capabilities?: { route: string | null; results: boolean; resultLetters: boolean; resultFaxes: boolean };
  /**
   * What the PERMANENT OPT-OUT RECORD says about this member, and what this console holds that is
   * NEWER (William's row 145, answered yes 14 September 2026: newest evidence for what the screen
   * SAYS, the record for what is ENFORCED).
   *
   * Null when the member is not in the record, when the record is not switched on, and when it could
   * not be read. Those three are deliberately not distinguished HERE: the member panel's job is to
   * show an opt-out when there is one, and the Opt-outs screen is where an unreadable record is
   * reported loudly rather than inferred from a missing line on someone's chart.
   */
  optOut?: {
    class: string; at: string; channel: string; source: string; enforced: boolean;
    newerEvidence: { kind: 'resubscribed' | 'opt-in'; at: string; note: string } | null;
    recordPublishedAt: string;
  } | null;
  /**
   * HIS 181: a member who said STOP and then came back is NOT opted out. Shown as HISTORY on its own
   * line, never inside the opt-out notice and never a reason to withhold contact. Null when the
   * member never came back, the switch is off, or the campaign's came-back file could not be read.
   */
  cameBack?: {
    kind: 'restart' | 'false-stop' | 'opt-in-only';
    stoppedAt: string; restartedAt: string; stopChannel: string; restartChannel: string;
  } | null;
}

/** GET /api/care/member-faxability: can this member's program fax anything to the PCP for them? */
export type FaxabilityReason = 'not-offered' | 'no-results' | 'no-faxable-result' | 'unknown';
export interface Faxability {
  memberId: string;
  faxable: boolean;
  reason: FaxabilityReason | null;
  /** True when the operator may record past the warning after confirming; false for not-offered. */
  confirmable: boolean;
  /** The member's distinct coarse results, newest first, as stored. */
  results: string[];
  /** The faxable result set the answer was judged against, as the Portal published it. */
  faxableSet: string[];
  source: 'registry' | 'floor' | 'none';
  /** L4 (16 Oct 2026): whether a result letter can be resent for this member (absent from an older server). */
  letter?: Letterability;
}

/**
 * L4 (William, 16 Oct 2026 about 10 PM: "block with a reason"): a result letter resend is refused for a
 * member whose results are only failed tests. The server judges it from the registry's sets and words the
 * refusal (`message`); the screen only shows it. Mirrors api/src/lib/faxable.ts.
 */
export interface Letterability {
  memberId: string;
  sendable: boolean;
  reason: 'only-failed-tests' | null;
  results: string[];
  faxableSet: string[];
  failedSet: string[];
  source: 'registry' | 'floor' | 'none';
  message: string;
}

/**
 * The faxable guard's operator wording (WARN-WITH-CONFIRM, built 2026-09-06). THE WORDING IS
 * WILLIAM'S CALL and this table is the one place it lives: the drawer renders these strings and
 * nothing else composes them, so his words drop in here without touching the mechanism. Drafted
 * from the rule as he stated it on 2026-07-24 ("we do not notify the PCP when we cannot test").
 */
export const FAX_NOT_FAXABLE_WORDING: {
  message: Record<FaxabilityReason, (fx: Faxability) => string>;
  confirmLabel: string;
  blockedLabel: string;
} = {
  message: {
    'no-faxable-result': (fx) =>
      `No fax will go to the PCP. This member's result on file is ${fx.results.join(' / ')}, and only ` +
      `${fx.faxableSet.join(' or ')} results are faxed: we do not notify the PCP when we cannot test. ` +
      'Tell the member their physician will not receive a fax.',
    'no-results': () =>
      'No fax will go to the PCP. This member has no result on file yet, so there is nothing to fax. ' +
      'Tell the member their physician will not receive a fax until a result exists.',
    unknown: () =>
      'It could not be checked whether this member has a faxable result (the campaign database did not answer). ' +
      'If their only result is Invalid or Unable to test, no fax will go to the PCP.',
    'not-offered': () =>
      'This program has no result faxes, so a PCP fax cannot be recorded for this member.',
  },
  confirmLabel: 'I have told the member no fax will be sent. Record the request anyway.',
  blockedLabel: 'Re-fax to PCP cannot be recorded for this member: the program has no result faxes.',
};

/**
 * THE DECLINE WORDING, and it is WILLIAM'S CALL. This table is the one place it lives: the drawer and
 * the review screen render these strings and nothing else composes them, so his words drop in here
 * without touching the mechanism. Same arrangement as FAX_NOT_FAXABLE_WORDING.
 *
 * The strings below are the PROGRAM OFFICE'S PROPOSED wording of 14 September 2026, 4:08 PM, built to
 * on the office's instruction while his own words are pending as row 143. They are a placeholder in
 * status, never in quality.
 *
 * The pair exists because the OPERATOR is the person who decides which one happened, in the moment,
 * on a call. The two sentences therefore state the CONSEQUENCE rather than the category: an operator
 * choosing between "decline" and "opt-out" is guessing at our vocabulary, while one choosing between
 * "still contactable" and "stop all contact" is answering a question about the member.
 */
export const DECLINE_WORDING: {
  actionLabel: string;
  requestLabel: string;
  choice: { decline: string; stop: string };
  scope: Record<'offer' | 'campaign', string>;
  reasonRequired: string;
} = {
  actionLabel: 'Declined this offer, still contactable',
  requestLabel: 'Declined this offer',
  choice: {
    decline: 'Declined this offer, still contactable',
    stop: 'Asked us to stop all contact',
  },
  scope: {
    offer: 'This kit or this test',
    campaign: "This programme's outreach",
  },
  reasonRequired: 'Say what the member declined, in their words where you have them.',
};

/**
 * THE APPROVAL LINE for the opt-out review screen, and it is WILLIAM'S CALL, in one place for the
 * same reason as the table above (office's proposed wording, 14 September 2026; his own is row 143).
 *
 * It exists because of a real misreading the screen would otherwise invite. A STOP is enforced
 * PROVISIONALLY AND ALWAYS from the moment it arrives, and the review can only ever LOOSEN. So
 * approving changes NOTHING the member experiences. Without this sentence on the screen, an admin
 * would reasonably believe the member is still being texted until they click approve.
 */
export const OPTOUT_REVIEW_WORDING: { approvalNote: string; reversalsNote: string; markerNote: string; overrideQueuedNote: string } = {
  approvalNote:
    "This STOP is already in force. Approving records that you read the member's words and agree; " +
    'nothing the member receives changes. Rejecting sends an un-block request with your reason.',
  /** The came-back list (his 168 and 181). Its own list; a row here is the opposite of a stop. */
  reversalsNote:
    'These members said STOP and then said START, or were found to be false stops. They are OFF the ' +
    'opt-out record and are contactable; nothing here is a stop and there is nothing to approve. Both ' +
    'times are kept so the campaign remembers they once stopped and came back.',
  /** The prior-campaign marker's enforcement sentence, both halves (his 181 and the office's 6:58 PM). */
  markerNote:
    'A STOP from any campaign, this year or last, is enforced today and next year. A member who ' +
    'restarted is not a stop and is not on this list.',
  /**
   * His 226 (16 Sep 2026): shown whenever the platform write (row 216) is OFF, because the approval
   * note's "Rejecting sends an un-block request" is only half true until then.
   */
  overrideQueuedNote:
    'Texting is not connected to this screen yet. "Not a stop" is recorded here with your reason, but ' +
    'it reaches texting only once this screen is connected. Until then the decision is ' +
    'queued and the member stays blocked.',
};

/**
 * The extract cohorts in words, keyed by the `source=` token of the published basis. A token with no
 * entry shows as itself: an unfamiliar cohort must read as unfamiliar, never as a familiar label.
 */
export const COHORT_WORDING: Record<string, { label: string; help: string }> = {
  'alexbol-2025': {
    label: '2025 texted STOPs',
    help: "Members who texted STOP to a 2025 Fall or Spring campaign text. Review each and override where it is not a stop.",
  },
};

/** The came-back kinds, in words. Derived on the API from the published fields (the file has no kind column). */
/** The prior-campaign marker in words. Three states; 'unknown' is a statement about the LOOKUP, never the member. */
export const CAMPAIGN_MARKER_WORDING: Record<'on-current' | 'not-on-current' | 'unknown', { label: string; help: string; pill: string }> = {
  'on-current':     { label: 'On current campaign',  help: 'This member is on the current campaign roster.', pill: 'pill-success' },
  'not-on-current': { label: 'Not on current',       help: 'Not on the current campaign roster: a prior campaign or another plan. The stop is enforced today and next year regardless.', pill: 'pill-warning' },
  'unknown':        { label: 'Cannot tell',          help: 'No member id on this row to look up, or the roster could not be read. This says nothing about the member.', pill: 'pill-accent' },
};

export const CAME_BACK_WORDING: Record<'restart' | 'false-stop' | 'opt-in-only', string> = {
  'restart': 'said STOP, then came back',
  'false-stop': 'a false stop: never a stop order',
  'opt-in-only': 'opted in; never stopped',
};

/**
 * THE OPTED-OUT TREATMENT (William's rows 144 and 145, both answered yes on 14 September 2026).
 *
 * He asked for "a grey shade for each member opted out". GREY ALONE IS NOT ENOUGH, and he agreed:
 * a colour is invisible to an operator on a bright screen, to one with a colour-vision difference,
 * on a printed list, and to a screen reader. So the row carries THREE signals of the same fact: the
 * shade, a pill that names it in words with the date, and an aria-label. Any one of them surviving
 * is enough for the operator to know.
 *
 * The wording is in this one table for the same reason FAX_NOT_FAXABLE_WORDING is.
 */
export const OPTED_OUT_WORDING = {
  pill: 'Opted out',
  /** Read by a screen reader in place of nothing at all. */
  aria: (at: string): string => (at ? `opted out on ${at}` : 'opted out'),
  /** Shown when the console holds something newer than the published record (his row 145). */
  newer: (recordAt: string, evidenceAt: string): string =>
    `The opt-out record says this member is opted out as of ${recordAt}. This console has something `
    + `newer, from ${evidenceAt}: they asked to come back. The suppression stays in force until the `
    + `record is republished or the review screen sends an un-block.`,
} as const;

/** The row background for an opted-out member. One place, so the queue and the lists cannot drift. */
export const OPTED_OUT_ROW_STYLE: { background: string; opacity: number } = {
  background: 'var(--paper-2)',
  opacity: 0.72,
};

/** Read-only 26fobt no-consent worklist row (GET /api/care/roster/no-consent). */
export interface RosterMember {
  memberId: string;
  fullName: string;
  phone1: string;
  phone2: string;
  email: string;
  language: string;
  lastActivity: string;
  consentState: 'none' | 'unknown';
}

export interface RosterResp {
  client: string;
  available: boolean;
  consentColumn: string | null;
  count: number;
  members: RosterMember[];
}

/* ------------- "Completed via another method" report (Alignment 2026-08) ------------- */

/** How an event signaled completed-elsewhere (api/care/report-completed-elsewhere.ts precedence). */
export type CompletedElsewhereVia = 'disposition' | 'member-request' | 'opt-out-reason';

/** One report row (GET /api/care/report/completed-elsewhere): one event carrying the signal. */
export interface CompletedElsewhereRow {
  eventId: string;
  receivedTs: string;
  channel: string;
  status: string;
  memberId: string;
  insuranceId: string;
  memberName: string;
  dob: string;
  phone: string;
  language: string;
  via: CompletedElsewhereVia;
  viaLabel: string;
  recordedTs: string;
  recordedBy: string;
  note: string;
  /** Row 354: null when the event names no campaign. */
  campaign?: string | null;
}

export interface CompletedElsewhereResp {
  client: string;
  count: number;
  memberCount: number;
  /** False = member_care.db was unavailable, so archived events are missing (live queue only). */
  archiveCovered: boolean;
  rows: CompletedElsewhereRow[];
}

/** PCP directory row (GET /api/care/pcp-search). No member PHI beyond the panel count. */
export interface PcpCandidate {
  npi: string;
  name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  fax: string;
  phone: string;
  panelCount: number;
}

export interface PcpSearchResp {
  emptyReason?: EmptyReason;
  client: string;
  q: string;
  available: boolean;
  count: number;
  pcps: PcpCandidate[];
  note?: string;
}

/** PHI-light panel-member row inside the reason-gated PCP reveal (POST /api/care/pcp-detail). */
export interface PcpPanelMember {
  member_id: string;
  full_name: string;
  dob: string;
  language: string;
  kit_stage: string;
  phone1: string;
}

export interface PcpDetailResp {
  found: boolean;
  available: boolean;
  pcp: PcpCandidate | null;
  /** Capped server-side; panelTotal is the true panel size (may exceed panel.length). */
  panel: PcpPanelMember[];
  panelTotal: number;
  /** Row 418: the PCP's letters and faxes (the Portal's PcpSection second half). Absent on an older API. */
  communications?: PcpCommunications | null;
}

/* ------------- Row 418: the PCP communications, the Enroute kits, the Results roster ------------- */

export interface PcpLetterRow {
  letter_id: string;
  member_id: string;
  full_name: string;
  /** The fax number the letter went to. */
  phone: string;
  pdf_mtime: string;
  delivery_status: string;
  delivered_ts: string;
}

export interface PcpFaxRow {
  fax_id: string;
  member_id: string;
  full_name: string;
  ts: string;
  direction: string;
  to_number: string;
  result: string;
  sub_result: string;
  pages: string;
  subject: string;
}

export interface PcpCommunications {
  available: boolean;
  unavailable?: string;
  lettersAvailable: boolean;
  faxesAvailable: boolean;
  letters: PcpLetterRow[];
  faxes: PcpFaxRow[];
  truncated: boolean;
}

/** One kit of the Enroute tab (POST /api/care/enroute-list), the Portal's Enroute row shape. */
export interface EnrouteKitRow {
  barcode_id: string;
  member_id: string;
  full_name: string;
  cohort: string;
  market: string;
  state: string;
  county: string;
  zip: string;
  gender: string;
  age: string;
  language: string;
  mso: string;
  ipa: string;
  comp_2024: string;
  comp_2025: string;
  has_email: number;
  has_phone: number;
  kit_stage: string;
  ship_date: string;
  outbound_tracking: string;
  outbound_status_category: string;
  outbound_status: string;
  delivered_to_member_ts: string;
  return_tracking: string;
  return_status_category: string;
  return_status: string;
  sample_shipped_ts: string;
  sample_received_ts: string;
  resulted_ts: string;
  last_scan_ts: string;
  do_not_solicit: string;
}

export interface EnroutePull {
  builtAt: string | null;
  trackingRefreshedAt: string | null;
  totalKits: number;
  delivered: number;
  shipped: number;
  received: number;
  exceptions: number;
  resulted: number;
}

export interface EnrouteListResp {
  emptyReason?: EmptyReason;
  available: boolean;
  truncated: boolean;
  total: number;
  count: number;
  rows: EnrouteKitRow[];
  pull: EnroutePull | null;
}

/** One resulted member of the Results tab (POST /api/care/results-list). Labels only, never codes. */
export interface ResultsRow {
  member_id: string;
  full_name: string;
  cohort: string;
  city: string;
  state: string;
  county: string;
  zip: string;
  gender: string;
  age: string;
  language: string;
  mso: string;
  ipa: string;
  comp_2024: string;
  comp_2025: string;
  has_email: number;
  has_phone: number;
  kit_stage: string;
  do_not_solicit: string;
  resulted_ts: string;
  pcp_faxed_ts: string;
  member_letter_ts: string;
  positive_handling_ts: string;
  result_label: string;
  status_label: string;
  status_meaning: string;
  is_positive: number;
}

export interface ResultsSummary {
  resulted: number;
  positives: number;
  byResult: Record<string, number>;
  byStatus: Record<string, number>;
  resultsIngestedAt: string | null;
  builtAt: string | null;
}

export interface ResultsListResp {
  emptyReason?: EmptyReason;
  available: boolean;
  total: number;
  count: number;
  rows: ResultsRow[];
  summary: ResultsSummary | null;
}

/* ------------- Shipping lookup (GET /api/care/shipping-search / -detail) ------------- */

/** Kit shipment row (kit_tracking_summary projection + member display name). */
export interface ShippingKitRow {
  kind: 'kit';
  member_id: string;
  full_name: string;
  barcode_id: string;
  ship_date: string;
  outbound_tracking: string;
  outbound_status: string;
  delivered_to_member_ts: string;
  return_tracking: string;
  return_status: string;
  sample_shipped_ts: string;
  sample_received_ts: string;
  last_scan_ts: string;
}

/** Mailed-letter row (member_result_letters projection + member display name). */
export interface ShippingLetterRow {
  kind: 'letter';
  member_id: string;
  full_name: string;
  letter_id: string;
  sent_date: string;
  tracking_code: string;
  carrier: string;
  delivery_status: string;
  delivered_ts: string;
}

export type ShippingRow = ShippingKitRow | ShippingLetterRow;

export interface ShippingSearchResp {
  q: string;
  count: number;
  available: boolean;
  kitsAvailable: boolean;
  lettersAvailable: boolean;
  /** The result set hit the server cap - the UI must say the list is partial. */
  truncated: boolean;
  rows: ShippingRow[];
  note?: string;
  emptyReason?: EmptyReason;
}

/**
 * The five typed empty states (mirrors api/src/lib/event-types.ts EmptyReason, 2026-09-06). A list
 * with no rows says WHY, so "no data" and "not reachable" never look alike to an operator.
 */
export type EmptyReason = 'no-rows' | 'query-too-short' | 'capability-absent' | 'roster-unavailable' | 'source-unreachable';

/** Notice above the table (null = no notice; the row text is enough), its tone, and the in-table row text. */
export const EMPTY_REASON_META: Record<EmptyReason, { notice: string | null; tone: 'warn' | 'danger'; row: string }> = {
  'no-rows': { notice: null, tone: 'warn', row: '' }, // row text comes from the caller (e.g. "No members match.")
  'query-too-short': { notice: null, tone: 'warn', row: 'Type at least 2 characters to search.' },
  'capability-absent': {
    notice: 'This program does not collect this kind of data, so its campaign database has no table for it. Nothing is missing.',
    tone: 'warn',
    row: 'Not part of this program.',
  },
  'roster-unavailable': {
    notice: 'Lookup is not available for this client on this deployment: no campaign database is configured for it.',
    tone: 'warn',
    row: 'Roster not available here.',
  },
  'source-unreachable': {
    notice: 'The campaign database could not be read just now. Nothing is wrong with the data; try again in a moment.',
    tone: 'danger',
    row: 'Could not be read.',
  },
};

/** The notice text for an empty answer, or the legacy fallback when the server sent no reason. */
export function emptyNotice(reason: EmptyReason | undefined, fallback: string): string {
  if (!reason) return fallback;
  return EMPTY_REASON_META[reason].notice ?? fallback;
}

export function emptyTone(reason: EmptyReason | undefined): 'warn' | 'danger' {
  return reason ? EMPTY_REASON_META[reason].tone : 'warn';
}

/** The in-table row text for an empty answer; `no-rows` and an unknown reason keep the caller's words. */
export function emptyRow(reason: EmptyReason | undefined, fallback: string): string {
  if (!reason) return fallback;
  return EMPTY_REASON_META[reason].row || fallback;
}

/** One USPS scan in the per-kit drill-down. */
export interface ShippingScan {
  tracking_number: string;
  direction: string;
  event_ts: string;
  event_code: string;
  event_type: string;
  city: string;
  state: string;
  zip: string;
}

export interface ShippingDetailResp {
  barcode: string;
  available: boolean;
  count: number;
  scans: ShippingScan[];
}

/* -------------------------------------------------------------------------- *
 * Browse-all Lookup lists (rework 2026-07-14, Portal Operations Lookup parity).
 * Whole-roster reads, one reason-gated POST per tab open; the client
 * virtualizes + searches in memory.
 * -------------------------------------------------------------------------- */

/** One roster row of the Member browse table (PHI-light; reachability is FLAGS,
 *  never raw contact data - the full record stays behind the reason-audited
 *  member-detail reveal). The demographic / program / screening fields feed the
 *  slicer rail's axes. */
export interface MemberBrowseRow {
  member_id: string;
  full_name: string;
  cohort: string;
  city: string;
  state: string;
  county: string;
  zip: string;
  market: string;
  gender: string;
  age: string;
  language: string;
  mso: string;
  ipa: string;
  comp_2024: string;
  comp_2025: string;
  has_email: number;
  has_phone: number;
  kit_stage: string;
  delivery_status: string;
  event_count: number;
  last_activity: string;
  /** THE OUTCOME FIELDS ARE OPTIONAL, AND ABSENT ON AN UNFILTERED BROWSE (QA-113 option 4,
   *  William 2026-09-06).
   *
   *  /api/care/member-list never sends them. They are merged onto a row here, in the browser, ONLY
   *  for the members returned by /api/care/member-results, which answers only behind the operator's
   *  own Result selection. So a row that carries an outcome is a row she asked about, and every
   *  other row has no outcome to render, sort or export.
   *
   *  Optional rather than removed, because the Status column and the charm helpers still render
   *  them for the asked-about rows; `CharmFields` has always declared them optional. */
  result_label?: string;
  status_label?: string;
  milestone?: '' | 'resulted' | 'delivered_to_lab';
  milestone_ts?: string;
  /** NOT an outcome: the 26fobt do-not-solicit mark is an outreach permission the operator must see
   *  before she contacts anyone, and it says nothing about a screening result. Always present. */
  do_not_solicit: string;
  outbound_tracking: string;
  return_tracking: string;
}

/** Counts per result label over the whole roster. Aggregates only: they name no member, which is
 *  why they are safe to send on an unfiltered browse and why the Result chips can survive without
 *  per-row outcomes. `available:false` means the facet read failed; the rail then hides the Result
 *  axes and SAYS why, rather than showing an empty pool that looks like "no member has a result". */
export interface MemberResultFacets {
  available: boolean;
  unavailable?: string;
  resultValue: Record<string, number>;
  resultStatus: Record<string, number>;
  milestone: Record<string, number>;
}

export interface MemberListResp {
  emptyReason?: EmptyReason;
  available: boolean;
  total: number;
  count: number;
  rows: MemberBrowseRow[];
  facets?: MemberResultFacets;
}

/** One member's outcome, as returned by /api/care/member-results for a named selection. */
export interface MemberOutcomeRow {
  member_id: string;
  result_label: string;
  status_label: string;
  milestone: '' | 'resulted' | 'delivered_to_lab';
  milestone_ts: string;
}

export interface MemberResultsResp {
  emptyReason?: EmptyReason;
  available: boolean;
  count: number;
  rows: MemberOutcomeRow[];
}

/** One aggregated row of the PCP browse table (Portal pcp-list KPI shape). The
 *  *_values columns are the slicer rail's panel-contains aggregates: each is the
 *  panel's DISTINCT set of a member-level dimension, char(31)-joined (split with
 *  splitMultiUS); result_values / result_flags are ','-joined coded tokens.
 *  NULL when the panel has no values (the axis self-hides). */
export interface PcpBrowseRow {
  pcp_name: string;
  pcp_npi: string;
  panel_size: number;
  delivered: number;
  returned: number;
  resulted: number;
  with_letter: number;
  top_mso: string;
  top_ipa: string;
  top_market: string;
  last_activity: string;
  county_values: string | null;
  state_values: string | null;
  zip_values: string | null;
  market_values: string | null;
  gender_values: string | null;
  age_values: string | null;
  language_values: string | null;
  cohort_values: string | null;
  mso_values: string | null;
  ipa_values: string | null;
  comp_pairs: string | null;
  kit_stage_values: string | null;
  delivery_values: string | null;
  email_values: string | null;
  phone_values: string | null;
  result_values: string | null;
  result_flags: string | null;
  any_dns: number;
}

export interface PcpListResp {
  emptyReason?: EmptyReason;
  available: boolean;
  total: number;
  count: number;
  rows: PcpBrowseRow[];
}

export interface ShippingListResp {
  emptyReason?: EmptyReason;
  available: boolean;
  kitsAvailable: boolean;
  lettersAvailable: boolean;
  /** The population hit the server cap - the UI must say the list is partial. */
  truncated: boolean;
  total: number;
  count: number;
  rows: ShippingRow[];
}

/* -------------------------------------------------------------------------- *
 * Display vocabulary - maps the model onto ClariCommand pill classes + labels.
 * -------------------------------------------------------------------------- */

/** Pill class is one of the kit's: '' (default ink) | pill-success | pill-warning | pill-danger | pill-primary | pill-accent. */
export const STATUS_META: Record<EventStatus, { label: string; pill: string }> = {
  new: { label: 'New', pill: 'pill-danger' },
  in_progress: { label: 'In progress', pill: 'pill-primary' },
  actioned: { label: 'Actioned', pill: 'pill-accent' },
  in_review: { label: 'In review', pill: 'pill-warning' },
  cleared: { label: 'Cleared', pill: 'pill-success' },
  spam: { label: 'Spam', pill: '' },
  'closed-no-action': { label: 'Closed (no action)', pill: '' },
  responded: { label: 'Responded', pill: 'pill-success' },
  closed: { label: 'Closed', pill: '' },
};

export const CHANNEL_META: Record<Channel, { label: string; pill: string }> = {
  SMS: { label: 'SMS', pill: 'pill-primary' },
  Email: { label: 'Email', pill: 'pill-accent' },
  Voice: { label: 'Voice', pill: 'pill-warning' },
  Referral: { label: 'Referral', pill: 'pill-success' },
  Unknown: { label: 'Unknown', pill: '' },
  // FOBT out-calling worklist channels (one per workbook tab, William 2026-07-09).
  'Opt-in Not Returned': { label: 'Opt-in Not Returned', pill: 'pill-primary' },
  'Last-Yr Lapsed': { label: 'Last-Yr Lapsed', pill: 'pill-accent' },
  'Screen + Not Returned': { label: 'Screen + Not Returned', pill: 'pill-warning' },
};

/** rev4 event-origin pills (queue Source column + source filter chips). */
export const SOURCE_META: Record<EventSource, { label: string; pill: string }> = {
  member: { label: 'Member', pill: '' },
  intake: { label: 'Intake', pill: 'pill-accent' },
  'print-station': { label: 'Print Station', pill: 'pill-warning' },
  portal: { label: 'Portal', pill: 'pill-primary' },
  pcp: { label: 'PCP', pill: 'pill-primary' },
  'out-calling': { label: 'Out calling', pill: 'pill-accent' },
  // Widened 2026-07-22: the three producer sources 274 live rows already carried (they rendered
  // through the SOURCE_META fallback as "Member" before - wrong origin on real rows).
  'recording-harvest': { label: 'Recording', pill: 'pill-warning' },
  'cs-address-collection': { label: 'Address collection', pill: 'pill-accent' },
  'cs-kit-reconciliation': { label: 'Kit reconciliation', pill: 'pill-accent' },
  // Widened 2026-09-05: the fax lane (Fax Station halt / Portal S5 refusal).
  'fax-lane': { label: 'Fax lane', pill: 'pill-danger' },
  // Widened 2026-09-19 (row 400 rev2): a Send to Main Dock referral, amber like the pill that made it.
  'opt-in-referral': { label: 'Opt In referral', pill: 'pill-warning' },
  // Widened 2026-09-22 (row 578b): a text the carrier refused, one entry per member and phone.
  'bad-number': { label: 'Bad or blocked number', pill: 'pill-danger' },
  // Widened 2026-09-26 (row 710): a member's email to ahp@, the address the platform's HELP answer gives.
  'help-email': { label: 'HELP email', pill: 'pill-warning' },
};

/**
 * Campaign display labels, slug to the name a person reads. Beside SOURCE_META because it does the
 * same job for the same table: the queue must never show a reader a bare slug like `26a1ckedgen`.
 *
 * A SLUG NOT IN THIS TABLE STILL RENDERS, as the slug itself, and that is deliberate: a campaign
 * created on the portal before this table is updated must appear on the queue rather than vanish
 * from it. The same fallback rule as SOURCE_META and as `snomedLabel`.
 */
export const CAMPAIGN_META: Record<string, { label: string; pill: string }> = {
  '26fobt': { label: '26 FOBT', pill: 'pill-accent' },
  // Added 2026-09-14, the day William registered them: "Setup 2 new campaigns for Alignment,
  // 26 A1c-KED General and 26 A1c-KED Intermountain." Slugs named by Frontend at 9:52 AM. Neither
  // slug is a prefix of the other, deliberately, so a prefix match can never confuse them.
  '26a1ckedgen': { label: '26 A1c-KED General', pill: 'pill-primary' },
  '26a1ckedim': { label: '26 A1c-KED Intermountain', pill: 'pill-primary' },
};

/**
 * What a row with NO campaign shows. PLACEHOLDER WORDING, William's to replace, kept in ONE constant
 * for exactly that reason: his words drop in here without touching a single mechanism, the same
 * arrangement as FAX_NOT_FAXABLE_WORDING.
 *
 * It is a NAMED STATE and never a blank or a dash. A dash reads as "nobody filled this in"; the truth
 * for a member's text or voicemail is that there is no campaign to know, which is a different and
 * permanent thing. The tooltip says so, because the column alone cannot.
 */
export const NO_CAMPAIGN_WORDING = {
  cell: 'No campaign',
  chip: 'No campaign',
  tooltip:
    'This contact is not linked to a campaign. A member text, email or voicemail names no campaign, '
    + 'and it is not guessed from the member, because a member can be in more than one.',
};

/** The campaign a row sorts and filters under; `none` is the shared token for a null campaign. */
export function campaignKeyOf(r: EventRow): string {
  return r.campaign || 'none';
}

/** The label a row shows in the Campaign column. */
export function campaignLabelOf(r: EventRow): string {
  if (!r.campaign) return NO_CAMPAIGN_WORDING.cell;
  return CAMPAIGN_META[r.campaign]?.label ?? r.campaign;
}

/**
 * R. 81 (William, 19 Sep 2026: "the drawer and UPDATE read every campaign"). The campaign ROSTER an
 * entry's member lookups read: the entry's own campaign, else the campaign the platform said the
 * reply answered, never the member's (a member can be in more than one campaign). The SAME rule as
 * `entryCampaign` in api/src/lib/reply-actions.ts, so what the drawer passes to member-search and
 * member-detail is what UPDATE reads on the server, and what the header says is what was read. Null
 * means the entry names no campaign and the server reads the client's reference campaign.
 */
export function entryRosterCampaign(ev: { campaign?: string | null; smsReply?: { campaignName?: string | null } | null }): string | null {
  const own = String(ev.campaign ?? '').trim().toLowerCase();
  if (/^[a-z0-9][a-z0-9-]{0,39}$/.test(own)) return own;
  return readReplyCampaign(ev.smsReply?.campaignName)?.campaign ?? null;
}

/** The cycle a bare campaign name stands for; the SAME constant as api/src/lib/reply-campaign.ts. */
export const FIRST_CYCLE = 'Opt-in 01';

/**
 * Slice B (his img7 and img8, 24 Sep 2026): the platform's campaign name read as a campaign slug and a
 * cycle, "26A1CKEDGEN Opt-in 02" -> 26a1ckedgen + "Opt-in 02", a bare slug -> the first cycle. The
 * screen's copy of `readReplyCampaign` in api/src/lib/reply-campaign.ts, pinned in lockstep case by case.
 */
export function readReplyCampaign(name: unknown): { campaign: string; cycle: string } | null {
  const s = String(name ?? '').trim().replace(/\s+/g, ' ');
  if (!s) return null;
  const cut = s.indexOf(' ');
  const head = (cut < 0 ? s : s.slice(0, cut)).toLowerCase();
  if (!/^[a-z0-9][a-z0-9-]{0,39}$/.test(head)) return null;
  if (cut < 0) return { campaign: head, cycle: FIRST_CYCLE };
  if (!/[0-9]/.test(head)) return null;
  const rest = s.slice(cut + 1);
  if (!/^[A-Za-z0-9][A-Za-z0-9 -]{0,39}$/.test(rest)) return null;
  return { campaign: head, cycle: rest };
}

/**
 * The name Marny reads for the roster a lookup used: the entry's campaign when it names one, else
 * the client's reference campaign (what the API reads when nothing is named), else the honest
 * placeholder. A slug not in CAMPAIGN_META renders as itself, the same fallback as campaignLabelOf.
 */
export function rosterLabel(entryCampaign: string | null, referenceCampaign: string | null | undefined): string {
  const slug = entryCampaign ?? referenceCampaign ?? null;
  if (!slug) return ROSTER_WORDING.unknownRoster;
  return CAMPAIGN_META[slug]?.label ?? slug;
}

/** The member section's words for WHICH roster it read. One table, so the wording changes in one place. */
export const ROSTER_WORDING = {
  /** The section header: "Member (26 A1c-KED General roster)". */
  header: (label: string) => `Member (${label} roster)`,
  /** The full-record line inside the section. */
  fullRecord: (label: string) => `Full record (${label} roster)`,
  /** When neither the entry nor the loaded client list names a campaign (the list has not landed). */
  unknownRoster: 'reference',
  /** The server refused the entry's campaign by name; the operator fixes the campaign, never the member. */
  campaignUnknown: (slug: string) =>
    `Campaign ${slug} has no roster on this deployment, so no member can be read for this entry. Check the campaign on the entry.`,
} as const;

/**
 * Suggested-action pills (the thread queue's "what is being asked for" column). Labels match
 * ACTION_META wherever the suggestion names a real typed action, so the operator reads the same word
 * in the queue and in the drawer's disposition list. The two compliance keys carry pill-danger:
 * an opt-out or a HELP reply is time-boxed, not discretionary.
 */
export const SUGGESTED_META: Record<SuggestedActionKey, { label: string; pill: string }> = {
  'replacement-kit': { label: 'Send kit', pill: 'pill-primary' },
  'result-letter-resend': { label: 'Send result', pill: 'pill-primary' },
  'change-of-address': { label: 'Update address', pill: 'pill-accent' },
  'opt-out': { label: 'Opt out', pill: 'pill-danger' },
  'consent-update': { label: 'Record consent', pill: 'pill-success' },
  'callback-scheduled': { label: 'Call back', pill: 'pill-warning' },
  'pcp-fax-resend': { label: 'Re-fax PCP', pill: 'pill-warning' },
  // 2026-08 (Alignment): "I already did the test" - nudges the operator to record the test-completed
  // request/disposition, which is what the completed-elsewhere report reads.
  'test-completed': { label: 'Test completed', pill: 'pill-success' },
  'help-reply': { label: 'Reply HELP', pill: 'pill-danger' },
};

/** Item 6 request-log labels (William 2026-07-08; widened + relabeled by the 2026-07-14 feedback
 * batch). Order is the checklist render order. */
export const REQUEST_META: Record<RequestType, { label: string }> = {
  'change-of-address': { label: 'Change of address' },
  'replacement-kit': { label: 'Replacement kit' },
  'opt-out': { label: 'Opt-out of program' },
  'opt-in': { label: 'Opt-in' },
  'do-not-contact': { label: 'Do Not Contact (DNC)' },
  decline: { label: DECLINE_WORDING.requestLabel },
  'general-inquiry': { label: 'General Inquiry' },
  'referred-to-client-services': { label: 'Referred to Client Member Services' },
  'test-completed': { label: 'Test Completed' },
  'result-letter-resend': { label: 'Resend result letter' },
  consent: { label: 'Consent' },
  'pcp-refax': { label: 'Re-fax results to PCP' },
  callback: { label: 'Schedule call-back' },
  'special-case': { label: 'Special case' },
  'training-question': { label: 'Training / use questions' },
};

export const REQUEST_TYPES = Object.keys(REQUEST_META) as RequestType[];

/**
 * The 2026-07-14 feedback dropdown vocabularies. Values are stable keys (they persist on records and
 * ride action data); labels are the operator-facing words from William's feedback table.
 */
export const OPT_OUT_REASONS: { value: string; label: string }[] = [
  { value: 'already-completed-test', label: 'Already completed the test' },
  { value: 'no-longer-interested', label: 'No longer interested' },
  { value: 'requested-by-text-stop', label: 'Requested by text (STOP)' },
  { value: 'moved-or-changed-plan', label: 'Moved / changed plan' },
  { value: 'other', label: 'Other' },
];

export const REPLACEMENT_KIT_REASONS: { value: string; label: string }[] = [
  { value: 'undeliverable-rts', label: 'Undeliverable / Bad Address (RTS)' },
  { value: 'lost-or-discarded', label: 'Lost or Discarded Kit' },
  { value: 'damaged', label: 'Damaged Kit' },
  { value: 'collection-error', label: 'Collection Error / Test Completed Incorrectly' },
  { value: 'other', label: 'Other' },
];

export const TEST_COMPLETED_METHODS: { value: string; label: string }[] = [
  { value: 'cdx-completed-en-route', label: 'CDX test completed / en route' },
  { value: 'other-method', label: 'Completed via another method' },
];

/**
 * Human provenance labels for every matchedBy value (matching v2). The drawer's Member section aux
 * reads these so an operator sees HOW the record was matched (contact-center screen-pop practice).
 */
export const MATCHED_BY_LABEL: Record<Exclude<MatchedBy, 'none'>, string> = {
  phone1: 'matched by phone',
  phone2: 'matched by phone (secondary)',
  email: 'matched by email',
  'known-contact': 'matched from a prior confirmed contact',
  'kit-barcode': 'matched by kit barcode',
  'insurance-id': 'matched by insurance ID',
  'member-id': 'matched by member ID',
  manual: 'matched manually',
};

export function matchedByLabel(matchedBy: MatchedBy): string {
  return matchedBy === 'none' ? '' : MATCHED_BY_LABEL[matchedBy] ?? `matched by ${matchedBy}`;
}

/**
 * The drawer banner for an ingest-time SMS compliance flag: 'help' is informational. NEVER an automatic
 * registry write - the resolution pipeline is the only opt-out path.
 *
 * PART K (William, 16 Oct 2026 about 11:22 PM, on "Member texted STOP - record an Opt-out?" with its button
 * "Record an Opt-out (SMS)" shown on a VOICE call: "remove this"). The opt-out banner and its button are gone.
 * It showed on a voice call because it keyed on suggestedAction 'opt-out' alone, which the voice line and the
 * intake reading also set for a caller asking out, while its words assumed a text. The opt-out stays
 * recordable through the queue's Opt-Out and Stop buttons and the disposition panel's Opt-out of program and
 * Do Not Contact; no stop detection changed.
 */
export function suggestedActionBanner(
  suggestedAction: SuggestedAction | undefined,
): { level: 'warn' | 'info'; text: string } | null {
  if (suggestedAction === 'help') {
    return { level: 'info', text: 'Member texted HELP - they may be asking for assistance.' };
  }
  return null;
}

/**
 * Item 12 disposition action labels + order (William 2026-07-08; widened + relabeled by the
 * 2026-07-14 feedback batch). 'special-case' (relabeled "Report to plan") is LAST per the annotated
 * screenshot; 'callback-scheduled' is "Schedule a follow-up call" (date + time). The key ORDER here
 * drives the item-12 checklist render order.
 *
 * crossesIsland = the disposition reaches the Portal in the resolution envelope. The Portal's action
 * vocabulary is CLOSED (8 types), so the 2026-07-14 additions are internal-only for now - except
 * 'do-not-contact', which rides translated to opt-out scope 'all' (the Portal's do_not_solicit
 * registry write IS permanent-DNC semantics) until the /1.1 vocabulary names it.
 */
export const ACTION_META: Record<ActionType, { label: string; crossesIsland: boolean }> = {
  'change-of-address': { label: 'Change of address', crossesIsland: true },
  'replacement-kit': { label: 'Replacement kit', crossesIsland: true },
  // crossesIsland FALSE until S5 accepts member-care-resolution/1.1; then it rides, HELD until a rail exists.
  'kit-request': { label: 'Kit request (first kit)', crossesIsland: false },
  'opt-out': { label: 'Opt-out of program', crossesIsland: true },
  'do-not-contact': { label: 'Do Not Contact (DNC)', crossesIsland: true },
  // crossesIsland FALSE, and that is the load-bearing half: there is no faithful translation of a
  // decline into the Portal's closed vocabulary, and every type it would accept suppresses the
  // member. See handoff-emitter.ts.
  decline: { label: DECLINE_WORDING.actionLabel, crossesIsland: false },
  'opt-in': { label: 'Opt-in', crossesIsland: false },
  'result-letter-resend': { label: 'Resend result letter', crossesIsland: true },
  'consent-update': { label: 'Consent update', crossesIsland: true },
  'test-completed': { label: 'Test Completed', crossesIsland: false },
  'general-inquiry': { label: 'General Inquiry', crossesIsland: false },
  'referred-to-client-services': { label: 'Referred to Client Member Services', crossesIsland: false },
  'callback-scheduled': { label: 'Schedule a follow-up call', crossesIsland: false },
  'pcp-fax-resend': { label: 'Re-fax results to PCP', crossesIsland: true },
  'special-case': { label: 'Report to plan', crossesIsland: true },
};

/**
 * William's console review (2 Oct 2026, image 14, his map "Care Station Build", part 2 Interface): the
 * Action / disposition list under his headings, as his answers of 2:04 to 2:09 AM left it. The SAME
 * action types as before; nothing new is recorded. The seven he dropped are DROPPED_ACTIONS; "Re-fax
 * results to PCP" is the fax tools' own (FAX_TOOLS_ACTIONS: their "Initiate re-fax now" stages it, the
 * office's decision of 2:24 AM). Every ActionType is in exactly one of the three lists (pinned by test).
 */
export const ACTION_GROUPS: ReadonlyArray<{ label: string; types: readonly ActionType[] }> = [
  { label: 'Change information', types: ['change-of-address'] },
  { label: 'Opting', types: ['opt-in', 'opt-out', 'do-not-contact'] },
  { label: 'Kits', types: ['replacement-kit'] },
  { label: 'Resulting', types: ['result-letter-resend'] },
  { label: 'Consent', types: ['consent-update'] },
];

/**
 * His answer on idea 6 (2 Oct 2026, the eight under Other: "drop all."): no longer offered in the
 * console. Records already made stay readable (ACTION_META keeps their labels for the action log).
 * The eighth, Re-fax results to PCP, is the fax tools' own (FAX_TOOLS_ACTIONS).
 */
export const DROPPED_ACTIONS: readonly ActionType[] = [
  'kit-request', 'decline', 'test-completed', 'general-inquiry', 'referred-to-client-services', 'callback-scheduled', 'special-case',
];

/** Staged from the PCP panel's fax tools, not from the action list ("Initiate re-fax now"). */
export const FAX_TOOLS_ACTIONS: readonly ActionType[] = ['pcp-fax-resend'];

/**
 * Action types the Portal treats as member-mutating: a resolution whose actions include one of these
 * is REJECTED when member is null (confirmed contract 4b.4). The drawer disables Send to review in
 * that state; special-case and callback-scheduled may go unmatched (informational-only).
 * do-not-contact rides as opt-out scope 'all' (a members registry write), so it needs a member too.
 */
export const MEMBER_MUTATING_ACTIONS: ReadonlySet<ActionType> = new Set<ActionType>([
  'change-of-address', 'consent-update', 'opt-out', 'do-not-contact', 'pcp-fax-resend',
  'replacement-kit', 'result-letter-resend',
]);

/** The lifecycle stage order, for the queue-position stepper. */
export const STAGE_ORDER: EventStatus[] = ['new', 'in_progress', 'actioned', 'in_review', 'cleared'];

export function stageIndex(status: EventStatus): number {
  const i = STAGE_ORDER.indexOf(status);
  if (i >= 0) return i;
  // Legacy / off-ramp statuses map onto the nearest canonical stage for display.
  if (status === 'responded') return STAGE_ORDER.indexOf('actioned');
  if (status === 'closed' || status === 'closed-no-action' || status === 'spam') return STAGE_ORDER.length - 1;
  return 0;
}

export function statusLabel(status: EventStatus): string {
  return STATUS_META[status]?.label ?? status;
}

/* -------------------------------------------------------------------------- *
 * Time helpers. TAT (turnaround time) is always derived, never stored as truth.
 * -------------------------------------------------------------------------- */

export function fmtTs(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function fmtDate(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function fmtTime(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

/** Human turnaround between two ISO timestamps. Returns '' when either side is missing/invalid. */
export function tat(fromIso: string | undefined, toIso: string | undefined): string {
  if (!fromIso || !toIso) return '';
  const a = new Date(fromIso).getTime();
  const b = new Date(toIso).getTime();
  if (Number.isNaN(a) || Number.isNaN(b) || b < a) return '';
  const sec = Math.round((b - a) / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  const rem = min % 60;
  if (hr < 24) return rem ? `${hr}h ${rem}m` : `${hr}h`;
  const day = Math.floor(hr / 24);
  const remHr = hr % 24;
  return remHr ? `${day}d ${remHr}h` : `${day}d`;
}

export function fmtDuration(sec: number | undefined): string {
  if (!sec || sec <= 0) return '';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * Whole minutes as a d/h/m string (the rev4 resolution-TAT display). Mirrors the API's tatMinutes
 * unit (event-ops.ts) so the queue column, the drawer, and the review page all read the same way.
 */
export function fmtMinutes(mins: number): string {
  if (!Number.isFinite(mins) || mins < 0) return '';
  if (mins < 60) return `${Math.round(mins)}m`;
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  if (h < 24) return m ? `${h}h ${m}m` : `${h}h`;
  const d = Math.floor(h / 24);
  const remH = h % 24;
  return remH ? `${d}d ${remH}h` : `${d}d`;
}

/**
 * Live resolution-TAT minutes for an event: frozen at sentToReviewTs once sent (William #11:
 * receipt -> send-to-review), else ticking against `now`. Returns null when receivedTs is missing.
 */
export function liveTatMinutes(
  receivedTs: string | undefined,
  sentToReviewTs: string | undefined,
  nowMs: number,
): number | null {
  const a = Date.parse(receivedTs ?? '');
  if (!Number.isFinite(a)) return null;
  const b = sentToReviewTs ? Date.parse(sentToReviewTs) : nowMs;
  if (!Number.isFinite(b) || b < a) return 0;
  return Math.round((b - a) / 60000);
}

/* -------------------------------------------------------------------------- *
 * Final Review column sorting (William 2026-07-08): every column sorts asc/desc.
 * The comparator lives here so it is unit-testable and reused; TAT uses the SAME
 * live-computed value the cell renders (receivedTs -> sentToReviewTs, else now).
 * -------------------------------------------------------------------------- */

export type SortKey =
  | 'date' | 'channel' | 'source' | 'client' | 'campaign' | 'from' | 'content'
  | 'member' | 'instruction' | 'actions' | 'stage' | 'status' | 'tat' | 'handledBy';
export type SortDir = 'asc' | 'desc';

/** Numeric sort magnitude for a row's resolution TAT (null-safe; open rows tick against nowMs). */
function rowTat(r: EventRow, nowMs: number): number | null {
  return r.tatMinutes ?? liveTatMinutes(r.receivedTs, r.sentToReviewTs || undefined, nowMs);
}

/**
 * Resolve one column's ordering with direction baked in. Blank/null values ALWAYS sort last (so a
 * sort surfaces real data, not empties) - the blank rule is NOT flipped by direction; only the
 * non-blank comparison is. `cmp` is the ascending comparison of two present values.
 */
function order(dir: SortDir, aBlank: boolean, bBlank: boolean, cmp: number): number {
  if (aBlank && bBlank) return 0;
  if (aBlank) return 1; // a -> last, regardless of dir
  if (bBlank) return -1; // b -> last, regardless of dir
  return dir === 'asc' ? cmp : -cmp;
}

/**
 * Compare two rows on one column, direction included. Strings are case-insensitive localeCompare;
 * date/stage/tat are numeric; blanks always sort last (see order()). TAT uses the live-computed value
 * so it agrees with the rendered cell.
 */
export function compareRows(a: EventRow, b: EventRow, key: SortKey, dir: SortDir, nowMs: number): number {
  const num = (x: number | null, y: number | null): number =>
    order(dir, x == null, y == null, (x ?? 0) - (y ?? 0));
  const txt = (x: string, y: string): number =>
    order(dir, !x.trim(), !y.trim(), x.localeCompare(y, 'en-US', { sensitivity: 'base', numeric: true }));
  switch (key) {
    case 'date': return num(Date.parse(a.receivedTs) || null, Date.parse(b.receivedTs) || null);
    case 'channel': return txt(a.channel, b.channel);
    case 'source': return txt(SOURCE_META[a.source]?.label ?? a.source, SOURCE_META[b.source]?.label ?? b.source);
    case 'client': return txt(a.client, b.client);
    // Sorted on the LABEL the column displays, not the slug, so "26 A1c-KED General" sorts where a
    // reader expects it rather than where `26a1ckedgen` happens to fall. No-campaign rows carry the
    // placeholder text, so `txt`'s empty-last rule does not apply to them: they group together under
    // whatever that wording sorts as, which is what an operator working that bucket wants.
    case 'campaign': return txt(campaignLabelOf(a), campaignLabelOf(b));
    case 'from': return txt(a.fromPhone || a.fromAddress, b.fromPhone || b.fromAddress);
    case 'content': return txt(`${a.subject} ${a.preview}`, `${b.subject} ${b.preview}`);
    case 'member': return txt(a.memberName || a.memberId, b.memberName || b.memberId);
    case 'instruction': return txt(
      a.instruction ? (ACTION_META[a.instruction as ActionType]?.label ?? a.instruction) : '',
      b.instruction ? (ACTION_META[b.instruction as ActionType]?.label ?? b.instruction) : '',
    );
    case 'actions': {
      // Sort the Final Review "Actions" column by the SAME distinct set it displays (sorted labels,
      // so order within the set does not change the sort and identical pill sets group together).
      const key = (r: EventRow) =>
        r.actions.map((x) => ACTION_META[x as ActionType]?.label ?? x).sort().join(', ');
      return txt(key(a), key(b));
    }
    case 'stage': return num(stageIndex(a.status), stageIndex(b.status));
    case 'status': return txt(statusLabel(a.status), statusLabel(b.status));
    case 'tat': return num(rowTat(a, nowMs), rowTat(b, nowMs));
    case 'handledBy': return txt(a.operatorName || a.assignedTo, b.operatorName || b.assignedTo);
    default: return 0;
  }
}

/** Sort a COPY of the rows by (key, dir); nowMs feeds the live TAT comparator so it agrees with the cell. */
export function sortRows(rows: EventRow[], key: SortKey, dir: SortDir, nowMs: number): EventRow[] {
  return [...rows].sort((a, b) => compareRows(a, b, key, dir, nowMs));
}

/* -------------------------------------------------------------------------- *
 * Review-batch archive (07 Archive, William 2026-07-08). Read-only view over the
 * dated station-archive bundles each Final Review batch approval writes. Mirrors
 * the API's review-archive.ts shapes.
 * -------------------------------------------------------------------------- */

/** One manifest row inside a bundle (member display name + stripped id only; no PHI snapshot). */
export interface ReviewBatchRow {
  eventId: string;
  memberId: string;
  memberName: string;
  actions: string[];
  operator: string;
  reviewedBy: string;
  receivedTs: string;
  sentToReviewTs: string;
  approvedTs: string;
  tatMinutes: number;
  handoffRef: string;
}

/** A bundle summary row for the Archive index. */
export interface ReviewBatchSummary {
  batchId: string;
  approvedTs: string;
  count: number;
  members: number;
  actions: { type: string; count: number }[];
  operators: string[];
  reviewers: string[];
  avgTatMinutes: number | null;
  hasManifest: boolean;
}

/**
 * Sortable Archive columns. Sorting is SERVER-side (the API orders the whole archive, then cuts the
 * page) because sorting the delivered page here would reorder only the loaded 60 of 236 - "highest
 * TAT" would quietly mean "highest TAT among the newest 60". Mirrors the API's REVIEW_BATCH_SORT_KEYS.
 */
export type ReviewBatchSortKey =
  | 'approved' | 'bundle' | 'members' | 'dispositions' | 'operators' | 'reviewers' | 'tat';

/**
 * One PAGE of the bundle index. Summarizing a bundle costs a round trip for its manifest, so the API
 * pages this (2026-08-03); `count` is what this page carried, `total` the whole archive. `sort`/`dir`
 * echo the order the server actually applied.
 */
export interface ReviewBatchesResp {
  client: string;
  count: number;
  total: number;
  offset: number;
  limit: number;
  sort: ReviewBatchSortKey;
  dir: SortDir;
  batches: ReviewBatchSummary[];
  /** Row 356: every key applied, the slicer counts over the whole archive, and what the slicers hide. */
  sorts?: Array<{ k: ReviewBatchSortKey; asc: boolean }>;
  facets?: Record<string, Array<{ v: string; n: number }>>;
  sliceHidden?: number;
}

export interface ReviewBatchResp {
  client: string;
  batchId: string;
  count: number;
  rows: ReviewBatchRow[];
}

/* -------------------------------------------------------------------------- *
 * Closed entries (R. 82 / row 401, William 19 Sep 2026: "Move the closed files to an Archive table
 * in the db for Care"). The second view of nav 08: every entry in a terminal status, read from
 * member_care.db, which holds every event live and archived. Mirrors the API's archived-events.ts.
 * -------------------------------------------------------------------------- */

/**
 * How the row's closer was found. The database does not always hold one, and a blank must never
 * read as "nobody", so the screen says it in words (CLOSED_VIA_WORDING).
 */
export type ClosedVia = 'status-history' | 'approval' | 'last-action' | 'none';

/**
 * A closed entry: the QUEUE ROW (the same PHI bound: member display name and stripped id, a
 * preview, never the snapshot) plus who closed it and when. The live-work signals the queue row
 * carries (doNotSolicit, optedOut, the reply reading) are ABSENT here, not false: the archive did
 * not check them, and a record that is finished with has no use for them.
 */
export type ArchivedEventRow = Omit<
  EventRow,
  'doNotSolicit' | 'optedOut' | 'laterRefusal' | 'answerChanged' | 'replyAnswer' | 'replyGroupHint' | 'replyNote' | 'replyReadFirst'
> & {
  closedBy: string;
  closedTs: string;
  closedVia: ClosedVia;
  archived: true;
};

/** The keys the server sorts the closed entries by (received is the default, newest first). */
export type ArchivedSortKey = 'received' | 'member' | 'status' | 'channel' | 'source' | 'closed';

export interface ArchivedEventsResp {
  client: string;
  count: number;
  total: number;
  offset: number;
  limit: number;
  sorts: Array<{ k: ArchivedSortKey; asc: boolean }>;
  facets: Record<string, Array<{ v: string; n: number }>>;
  sliceHidden: number;
  /** The builder's `build_ingested_at`: how fresh the table is. Null when it could not be read. */
  dbAsOf: string | null;
  items: ArchivedEventRow[];
  emptyReason?: EmptyReason;
}

/** The closed-entries screen's words, in one place. */
export const ARCHIVE_ENTRIES_WORDING = {
  tabBundles: 'Review bundles',
  tabEntries: 'Closed entries',
  title: 'Archive (closed entries)',
  note: 'Closed entries move here 10 minutes after closing; the queue shows open entries only.',
  lede: 'Every entry that has been closed: cleared at review, marked spam, closed, or closed with no action. Read-only; open a row to see the record as it was closed.',
  searchPlaceholder: 'Search name / member ID / phone / text',
  fresh: (when: string) => `Table as of ${when}.`,
  unreadable: 'The archive table could not be read just now. Nothing is wrong with the data; try again in a moment.',
  noRows: 'No closed entries match.',
  nothingYet: 'Nothing has been closed yet.',
  notRecorded: 'Not recorded',
  readOnly: 'Archived entry. Read-only: this entry was closed and moved to the archive, so nothing here can be changed.',
} as const;

/** How the Closed by cell reads for each way the closer was found. */
export const CLOSED_VIA_WORDING: Record<ClosedVia, { hint: string }> = {
  'status-history': { hint: 'From the status history: the transition that closed it.' },
  approval: { hint: 'From the review approval: the reviewer and the clear time.' },
  'last-action': { hint: 'No closing transition on the record; this is the last action recorded on it.' },
  none: { hint: 'The record carries nothing that names who closed it or when.' },
};

/* ------------------------------------------------------------------------------------------------
 * SMS rail (cdx-sms/1.0). A conversation belongs to the MEMBER, not to an event: the member sees one
 * thread on one phone and cannot address a reply to a particular agent, so no inbound message can be
 * attributed to the event that provoked it. Events stay the unit of work; the transcript sits beside
 * them and is shared by every operator working that member.
 * ---------------------------------------------------------------------------------------------- */

/** Delivery state of a historical OUTBOUND row, straight from the mirror. */
export type SmsDeliveryStatus = 'SENT' | 'DELIVERED' | 'QUEUED' | 'REJECTED';

/** One row of the member's SMS transcript, projected from cdx-sms `/sms/thread`. The console holds
 *  these in session state only - there is no local store (no-DB / Option A). */
export interface SmsMessageRecord {
  messageId: string;
  direction: 'in' | 'out';
  ts: string;
  body: string;
  /** ISO 639-1 language of THIS message (cdx-sms detect; en/es/vi/zh, or an authoritative ISO for a
   *  translated inbound). Absent when the platform sent none. Drives the per-message language chip. */
  detectedLang?: string;
  /** English rendering of an inbound non-English message, present ONLY when the transcript was pulled
   *  with the Translate toggle on. Absent on outbound, on English, and when translation is off. */
  translatedText?: string;
  // OUTBOUND
  deliveryStatus?: SmsDeliveryStatus;
  operator?: string;       // bare UPN for a console send; '' for a campaign / auto message
  operatorName?: string;   // display: the UPN, or a campaign label ("Automated reminder")
  automated?: boolean;     // true = campaign / auto-reply, not a console operator
  templateKey?: string;
  deliveredTs?: string;
  acsMessageId?: string;
  // INBOUND
  keyword?: string;
  actedOptOut?: boolean;
  fromPhone?: string;
}

export interface SmsSuppressionRow {
  phone: string;
  reason: string;
  active: boolean;
  optedOutTs: string | null;
  resubscribedTs: string | null;
}

/**
 * One Claire interaction (a voice call, or a text on the Claire line) in the unified member thread
 * (fleet contract 2026-07-11). Call-level granularity: `language`/`lang`/translation describe the
 * whole interaction, where an SMS row's detectedLang/translatedText describe one message. `language`
 * is Claire's display name verbatim ("Spanish", "Korean"); `lang` is the canonical ISO 639-1 from
 * the console-owned map ('und' for a name outside the four - flagged non-English, never read as en).
 * The translation fields are always present when the payload carried them; the Translate toggle
 * gates DISPLAY only (voice translation costs nothing extra, unlike the mirror's ?translate=1).
 */
export interface ClaireInteractionRecord {
  eventId: string;
  ts: string;
  channel: 'voice' | 'sms';
  subject: string;
  summary: string;
  reason: string;
  requestType: string;
  language: string;
  lang: string;
  hasAudio: boolean;
  claireResolved?: boolean;
  translatedReason?: string;
  translatedTranscript?: string;
}

export interface SmsConversationResp {
  memberId: string;
  memberName: string;
  language: string;
  phones: string[];
  /** Row 847: the record's two numbers, labelled, for the Text box's number picker ('' = none usable).
   *  Optional: a response without it leaves the Text box exactly as it was before the picker. */
  recordPhones?: { phone1: string; phone2: string };
  messages: SmsMessageRecord[];
  /** The member's Claire history (voice + Claire-line texts), oldest->newest. */
  claireInteractions: ClaireInteractionRecord[];
  /** false = the event store was unreachable; render "call history unavailable", NEVER an empty history. */
  claireComplete: boolean;
  suppressed: boolean;
  suppressedPhones: string[];
  suppressionRows: SmsSuppressionRow[];
  suppressionAsOf: string;
  /** false = the suppression feed was unreachable. NOT the same as "clear to text". */
  suppressionKnown: boolean;
  /** 26fobt do-not-solicit marker (a timestamp string) for a matched member; '' when unmarked/unknown.
   *  Positive-only: '' is NOT "safe to contact" (26fobt.do_not_solicit is known-incomplete). */
  doNotSolicit: string;
  canSend: boolean;
  smsConfigured: boolean;
  /** True once served from the live mirror (every direction present); false only when the rail is
   *  not configured. The old store-backed route was hardwired false for lack of an outbound feed. */
  transcriptComplete: boolean;
  /** The after-hours hold is on (or in test): the screen asks for the held list. Absent = today's screen. */
  smsHold?: boolean;
}

export interface SmsSendResp {
  /** 'held' (the after-hours hold): outside texting hours, kept to send by itself at heldUntil. NOT sent. */
  status: 'sent' | 'suppressed' | 'quiet_hours' | 'unknown' | 'error' | 'held';
  delivered: boolean;
  /** status 'held' only: when the release is first tried (ISO), and the member's zone the estimate used. */
  heldUntil?: string;
  memberZone?: string;
  reason?: string;
  acsMessageId?: string;
  clientRequestId?: string;
  /** 'same-id' = a transport-unknown; press Send again (replays). 'new-id' = a definite non-send;
   *  mint a fresh clientRequestId to retry. */
  retry?: 'same-id' | 'new-id';
  ts?: string;
}

/** The after-hours hold's states (api/src/lib/sms-held.ts). Only 'sent' means the member was texted. */
export type HeldSmsState = 'held' | 'releasing' | 'sent' | 'not-sent-opted-out' | 'failed' | 'cancelled';

/** One held text, as GET /api/care/sms/held answers it. */
export interface HeldSmsRecord {
  clientRequestId: string;
  text: string;
  phone: string;
  memberId: string;
  contextEventId: string;
  operator: string;
  operatorName: string;
  heldAt: string;
  sendAfter: string;
  memberZone: string;
  state: HeldSmsState;
  outcome: { status: string; at: string; reason: string } | null;
}

export interface HeldSmsListResp {
  enabled: boolean;
  mode: string;
  items: HeldSmsRecord[];
}

/** One row of the unread-text signal feed. PHI-minimized: identity + timing, never the body. */
export interface SmsInboundSignalRow {
  messageId: string;
  fromPhone: string; // last-10; the key the console badges on
  receivedTs: string;
  keyword: string | null;
}

export interface SmsInboundResp {
  configured: boolean;
  rows: SmsInboundSignalRow[];
  nextCursor: string | null;
}

/* ------------------------------------------------------------------------------------------------
 * Language display (cdx-sms ?translate + the future voice+SMS unified thread). The SMS mirror and
 * Claire's voice feed (claire-interaction/1) both key language on ISO 639-1, so ONE label map serves
 * both. The cdx-sms heuristic emits en/es/vi/zh; translate=1 can return any ISO for a non-English
 * inbound (sky2-zit 2026-07-11). An unknown code falls back to its uppercased self, never blank.
 * ---------------------------------------------------------------------------------------------- */
export const LANG_LABEL: Record<string, string> = {
  en: 'English', es: 'Spanish', vi: 'Vietnamese', zh: 'Chinese',
  ko: 'Korean', tl: 'Tagalog', ru: 'Russian', ja: 'Japanese',
  ar: 'Arabic', fr: 'French', pt: 'Portuguese', de: 'German', hi: 'Hindi',
};

/** Display label for an ISO 639-1 code ('' for empty). Unknown codes read as their uppercased self. */
export function langLabel(iso: string | undefined | null): string {
  const code = (iso ?? '').trim().toLowerCase();
  if (!code) return '';
  return LANG_LABEL[code] ?? code.toUpperCase();
}

/** True when a detected language is a non-English one worth flagging (the chip + the Translate offer). */
export function isNonEnglish(iso: string | undefined | null): boolean {
  const code = (iso ?? '').trim().toLowerCase();
  return code !== '' && code !== 'en';
}

/* ------------------------------------------------------------------------------------------------
 * Phone display + click-to-call (ergonomics roll-up, 2026-07-11). One formatter for every surface
 * (queue From column, drawer, SMS panel) so a number always reads the same way, and a Teams deep
 * link so the operator can place an outbound call without retyping the number.
 * ---------------------------------------------------------------------------------------------- */

/** US display format for a phone: 10 digits -> (xxx) xxx-xxxx; anything else verbatim. */
export function fmtPhone(phone: string | undefined | null): string {
  const raw = (phone ?? '').trim();
  const digits = raw.replace(/\D/g, '');
  const ten = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (ten.length !== 10) return raw;
  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
}

/**
 * Teams click-to-call deep link for a US phone, or null when the number is not dialable. The
 * `4:` prefix marks a PSTN participant; Teams opens pre-staged to place the call (the operator
 * still clicks Call there - nothing dials automatically). Requires the operator's Teams to have
 * PSTN calling, which is how the team places member calls today.
 */
export function teamsCallUrl(phone: string | undefined | null): string | null {
  const digits = (phone ?? '').replace(/\D/g, '');
  const e164 = digits.length === 10 ? `+1${digits}`
    : digits.length === 11 && digits.startsWith('1') ? `+${digits}` : '';
  if (!e164) return null;
  // e164 is [+][digits] only - URL-safe verbatim; Teams expects the raw +, not %2B.
  return `https://teams.microsoft.com/l/call/0/0?users=4:${e164}`;
}

/* ------------------------------------------------------------------------------------------------
 * The DO NOT CONTACT master table (William's row 286, 17 Sep 2026, 1:13 PM). ONE TABLE OF WORDS,
 * the same arrangement as FAX_NOT_FAXABLE_WORDING: the mechanism (a removal is the platform unblock
 * plus a CLEAR in the care decisions record; an adjustment changes no enforcement) is fixed, and
 * these are the sentences the admin reads. His own words drop in here without touching the code.
 * ---------------------------------------------------------------------------------------------- */
export const DNC_WORDING = {
  lede: 'Every person who asked not to be contacted, one row each: who, how it came about, what they said and when. Remove a name and every text and call from that moment passes; the mark on their member file clears when the file is next updated.',
  removeConfirm: (who: string, phone: string) =>
    `Remove ${who} (${phone}) from the DO NOT CONTACT list?\n\nFrom this moment the platform will text and call this number again, and the mark on the member's file clears when the file is next updated. Nothing is deleted: the row stays on the list as removed, with your reason.`,
  removed: 'Removed. Texts and calls to this number pass from now; the mark on the member file clears when the file is next updated.',
  removedCarrierHolds: 'Removed on our side, but the carrier still holds this number as opted out: texts will bounce until the member texts START to the 833 number.',
  platformOff: 'Removals cannot be sent from this screen yet. Nothing was changed; the member stays on the list.',
  /**
   * T-1025-CARE-07 (the office's 2:12 AM ruling, point 2): a batch the platform refused outright, said once. `why` is
   * fixed words or Care's own (the API's platformError); it read "The platform said: the platform refused it" before.
   */
  batchRefused: (why: string) =>
    `Not done: ${why.trim().replace(/\.+$/, '')}. Nothing was written, and the same request would be refused again, so change it rather than retrying it.`,
  notEnabled: 'This list is not turned on yet.',
  reasonRequired: 'A reason of at least 10 characters is required: it is the audit, on the platform and here.',
  onFile: 'On their file: the campaign database carries the do-not-contact notation.',
  onFileClears: 'On their file until the file is next updated: the removal is recorded and the mark clears then.',
  notOnFile: 'Not on their file: the campaign database carries no do-not-contact notation for this member.',
  noMember: 'No member file: this number is on no member list we can read, so this row is the only mark.',
  fileUnknown: 'The campaign database could not be read, so whether the notation is on their file is not known.',
  /** The same five answers in a few words, for the table cell (walk finding 5, 17 Sep: the full
   *  sentence in a narrow column made every row about ten lines tall). The sentence above stays the
   *  cell's tooltip and is listed once in the legend under the table, so nothing is lost. */
  fileShort: {
    onFile: 'On file',
    onFileClears: 'On file until the next update',
    notOnFile: 'Not on file',
    noMember: 'No member file',
    fileUnknown: 'Not known',
  },
  /* The "Mark as Do Not Contact" button in the case drawer's details pane (his 1:43 PM word). */
  markLabel: 'Mark as Do Not Contact',
  markScope: 'All channels: texts, calls and mail, every program, permanently. A stop on one channel only is the Opt-out action below.',
  markReasonRequired: 'A reason is required: it becomes the words on the master list and the notation on the member\'s record, with your name.',
  markConfirm: (who: string, phone: string, user: string) =>
    `Mark ${who} (${phone}) as DO NOT CONTACT on every channel?\n\nThis goes on the master list and on the member's record, both naming ${user}. Only a removal from the Do not contact table takes it off.`,
  markedBy: (user: string, at: string) => `Marked Do Not Contact by ${user} on ${at}.`,

  /* ------------------------------------------------------------------------------------------
   * ROW 300 (William, 17 Sep 2026 2:28 PM): the four "time-out" tables. ONE table of words, so a
   * tab, a banner, a confirmation and a refusal cannot call the same list by four different names.
   * Anyone on any of them is not contacted on any channel until Marny moves them off.
   * ---------------------------------------------------------------------------------------- */
  /* ROW 325 (17 Sep 2026 8:2x PM): his four EXCEPTION lists, one nav tab each. The 1.1 names stay
     below them because a case's recorded action may still carry one, and it must still read. */
  kindLabel: {
    dnc: 'Do not contact', optout: 'Opt-outs', delivery: 'USPS rejections', technical: 'Technical exceptions',
    address: 'Address issues', phone: 'Phone issues', duplicate: 'Duplicates',
  } as Record<string, string>,
  kindLede: {
    dnc: 'Every person who asked not to be contacted at all, one row each: who, how it came about, what they said and when. Every campaign, every channel.',
    optout: 'Every member who opted out of ONE campaign: they are not contacted for that campaign, and stay contactable for the others.',
    delivery: 'Every member whose address we cannot mail to: returned mail, a delivery failure, an address a check could not verify.',
    technical: 'Everything else that stops us contacting a member: no usable phone, a number that is not dialable, one phone on several members.',
    address: 'Every member whose address we cannot mail to.',
    phone: 'Every member we cannot reach by phone.',
    duplicate: 'Every member caught by a duplicate.',
  } as Record<string, string>,
  /** Row 325: how hard each list stops, said once per tab. */
  kindScope: 'Do not contact, USPS rejections and Technical exceptions stop every channel and every campaign; an opt-out stops ONE campaign only. A locked row stays in force; it only leaves the main table.',
  /** The short words for tab 13's issue chips (the platform's word shown as it came when not here). */
  issueLabel: { phone: 'Phone', duplicate: 'Duplicate', 'no-phone': 'No phone', address: 'Address', other: 'Other' } as Record<string, string>,
  /* ROW 325's MOVE and the locked view. */
  moveConfirm: (n: number, from: string, to: string, campaign: string | null) =>
    `Move ${n} row${n === 1 ? '' : 's'} from ${from} to ${to}${campaign ? ` for ${campaign}` : ''}?\n\n`
    + (campaign
      ? `They will be stopped for ${campaign} ONLY, and every other campaign may contact them again. A member who texted STOP stays blocked by the carrier for texts until they text START.`
      : 'They stay stopped on every channel and every campaign; only the list they are on changes.')
    + ' Each row keeps its history and your reason.',
  pickCampaign: 'Pick the campaign the opt-out is for.',
  noCampaigns: 'The platform named no current campaigns, so there is no campaign to opt out of yet.',
  lockedView: 'Locked rows stay in force and are kept off the main table. Unlock one, with a reason, to remove or move it.',
  /* ROW 327: the campaign selector. */
  allCampaigns: 'All campaigns',
  campaignHides: (outside: number | null, campaign: string) =>
    outside === null
      ? `Only members in ${campaign} are shown. Choose All campaigns to see everyone.`
      : `Only members in ${campaign} are shown; ${outside} row${outside === 1 ? '' : 's'} across the exception lists ${outside === 1 ? 'is' : 'are'} outside it. Choose All campaigns to see everyone.`,
  /** The banner on a case drawer, and the member's notation per kind. */
  markedOn: (kind: string, user: string, at: string) =>
    kind === 'dnc' ? `Marked Do Not Contact by ${user} on ${at}.` : `On the ${DNC_KIND_WORD[kind] ?? kind} list since ${at} by ${user}.`,
  lockedNote: (by: string | null, at: string, why: string | null) =>
    `Locked${by ? ` by ${by}` : ''}${at ? ` on ${at}` : ''}${why ? `: ${why}` : ''}. It has to be unlocked before it can be removed.`,
  lockConfirm: (n: number) =>
    `Lock ${n} row${n === 1 ? '' : 's'}?\n\nA locked row cannot be removed from the list until someone unlocks it and says why. Nothing else changes.`,
  unlockConfirm: (n: number) =>
    `Unlock ${n} row${n === 1 ? '' : 's'}?\n\nThey can be removed from the list again from this moment. Your reason is kept with the row.`,
  bulkRemoveConfirm: (n: number, list: string) =>
    `Remove ${n} row${n === 1 ? '' : 's'} from ${list}?\n\nFrom this moment the platform will text and call those numbers again, and the mark on each member's file clears when the file is next updated. Nothing is deleted: every row stays on the list as removed, with your reason.`,
  bulkMarkConfirm: (n: number, list: string, user: string, lock: boolean) =>
    `Put ${n} member${n === 1 ? '' : 's'} on ${list}?\n\nThey will not be contacted on any channel until someone takes them off${lock ? ', and the rows will be locked so they cannot be taken off by accident' : ''}. This goes on the master list and on each member's record, both naming ${user}.`,
  /** A row already on a list under another kind: the platform's own refusal, said in words. */
  alreadyListed: 'already on a list: one person holds one row, so Move them from the list they are on, or take them off it first',
  noSelection: 'Select at least one row first.',
  lockedRefusal: 'That row is locked, so it was not removed. Unlock it first, with a reason.',
  evidenceNone: 'none given',

  /* ------------------------------------------------------------------------------------------
   * THE REVIEW STATE (ClariConnect's 1.1 addendum, 5:05 PM, 81e3b6d). A review row is on a list for
   * the admin to judge and BLOCKS NOTHING: texts and calls pass, and nothing is on the member's file.
   * Every sentence below exists because the ordinary wording would tell her the opposite.
   * ---------------------------------------------------------------------------------------- */
  channelWords: (ch: string): string =>
    ch === 'both' ? 'texts and calls' : ch === 'sms' ? 'texts only' : ch === 'voice' ? 'calls only' : 'blocks nothing',
  reviewPill: 'For review',
  reviewNote: 'Listed for review: this row blocks nothing yet, so texts and calls still pass. Marking the person on a list is what stops them.',
  /** The note in two words under the pill; the sentence above is its tooltip and is in the legend. */
  reviewShort: 'Blocks nothing',
  /** Approval 130: the fold over a seeded row's batch line, and what the How column calls such a row. */
  showAll: 'show all',
  seededMethod: 'From an earlier list',
  removeConfirmReview: (who: string, phone: string) =>
    `Remove ${who} (${phone}) from the review list?\n\nThis row blocked nothing, so nothing changes for the member: texts and calls already pass, and there is no notation on their file to clear. Nothing is deleted: the row stays on the list as removed, with your reason.`,
  removedReview: 'Removed from the review list. Nothing changes for the member: the row blocked nothing and put nothing on their file.',
  removeTitleReview: 'Takes the row off the review list: nothing changes for the member, because the row blocked nothing.',
  sharedBy: (n: number) => `This phone number is on ${n} member${n === 1 ? '' : 's'}.`,
  /* ROW 359 A: a shared phone in plain words, said once, then the evidence as a small table. */
  sharedMeans: 'Texts to this number are stopped because we cannot tell who would read them.',
  sharedDo: 'To release it, confirm which member uses this phone.',
  /* ROW 358 point 5 and ROW 357: plain column words. */
  noMemberHasNumber: 'No member has this number',
  fromMembershipFile: "from the plan's member list",
  confirmAddress: 'Confirm the address with the member.',
  showMessages: (n: number) => `Show messages (${n})`,
  technicalDetail: 'Technical detail',
  whyHeading: { dnc: 'Why on the list', optout: 'Why on the list', delivery: 'Address', technical: 'What is wrong' } as Record<string, string>,
} as const;

/**
 * APPROVAL 130 (office, 17 Sep 2026 8:04 PM, on ClariConnect's 7:59 PM answer): what "What they said"
 * leads with, and whether `response` folds behind "show all". ONE decision, here, so the screen
 * cannot fold one row and not its twin.
 *
 * A SEEDED row (the platform's method `seed`) carries the check's plain line in `reason` and, in
 * `response`, ClariConnect's 200 to 340 character batch line, which is not anything a member said:
 * that line is what stood rows ten lines tall. So a seeded row leads with the reason and folds the
 * response. Nothing is dropped; the fold opens.
 *
 * Every other row is untouched, and that is the half that matters: a member's own words are shown
 * in full, never folded (his 1:43 PM word, "the full dialog", never summarised). A seeded row with no
 * reason also keeps its response in view, because folding it would leave the cell empty.
 */
export function dncSaidLayout(r: { seeded: boolean; reason: string | null; response: string | null }): { lead: string | null; foldResponse: boolean } {
  if (r.seeded && r.reason) return { lead: r.reason, foldResponse: !!r.response };
  return { lead: null, foldResponse: false };
}

/**
 * A platform refusal word in a sentence the admin can act on, with the word itself kept beside it so
 * the platform's log and the screen can still be matched. An unknown word is shown as it came, never
 * guessed at. The two hold-table words are ClariConnect's (5:22 PM): two conditions, one cause, and
 * both vanish the moment the ContactHold table is created, so the sentence covers the gap only.
 */
const DNC_REFUSAL_SENTENCE: Record<string, string> = {
  already_listed: DNC_WORDING.alreadyListed,
  locked: 'locked, so it was not moved or removed; unlock it first, with a reason',
  already_removed: 'already off the list',
  no_such_row: 'no longer on the list',
  same_kind: 'already on that list',
  unknown_campaign: 'not one of the campaigns the platform runs today',
  // ClariConnect's move refusals (9:55 PM). store_kind_not_enabled lasts until his schema change widens
  // the hold table's kinds, so it is said as a wait, not a fault.
  campaign_required: 'an opt-out needs the campaign it is for',
  campaign_not_allowed: 'only an opt-out names a campaign',
  store_kind_not_enabled: 'the platform cannot hold this list yet, so nothing was written',
  no_store_for_member_rows: 'the hold table is not created yet, so this row was not written',
  no_hold_store: 'the hold table is not created yet, so this row was not written',
};
export function dncRefusalWords(word: string): string {
  const s = DNC_REFUSAL_SENTENCE[word];
  return s ? `${s} (${word})` : word;
}

/** The words inside a sentence ("On the Address Issues list"), which are not the tab labels. */
const DNC_KIND_WORD: Record<string, string> = {
  dnc: 'DO NOT CONTACT',
  optout: 'Opt-out',
  delivery: 'USPS rejections',
  technical: 'Technical exceptions',
  address: 'Address Issues',
  phone: 'Phone Issues',
  duplicate: 'Duplicates',
};

/**
 * ROW 372 (William, 18 Sep 2026): tab 13's "Add a member to technical exclusion". EVERY sentence the
 * panel says is here, in plain words, so a label, a confirmation and an answer cannot call the same
 * thing by two names. The reason labels are keyed by the server's closed set of codes.
 */
export const TECH_EXCLUSION_WORDING = {
  title: 'Add a member to technical exclusion',
  lede: 'For a member who should get nothing more from us, for example someone who has died or gone into hospice. Find them, check who they are, pick the reason, and add them. Every channel stops: the member is held by member ID and every phone on their record is blocked.',
  searchLabel: 'Find a member',
  searchPlaceholder: 'Name, member ID or phone',
  searchButton: 'Find',
  searchTooShort: 'Type at least 2 characters.',
  searching: 'Looking...',
  noMatches: 'No member matches that search.',
  searchFailed: (why: string) => `The member search did not answer (${why}). Try again.`,
  choose: 'Choose',
  chosenHeading: 'Member',
  memberId: 'Member ID',
  birthYear: 'Year of birth',
  noBirthYear: 'Not on file',
  phonesHeading: 'Phones',
  noPhones: 'No phone on file for this member. The member is held by member ID; there is no number to block.',
  phoneOnlyThis: 'Only this member has this phone. It is blocked: every text to it stops.',
  // "Both rows" (William, 19 Sep 2026): a shared phone is blocked TOO, and blocking the number stops
  // the other member's texts as well (his row 312 rule), so the line says so before the press.
  sharedPhone: (n: number) =>
    `This phone is also on ${n} other member${n === 1 ? '' : 's'}. It is blocked too, which stops their texts as well.`,
  sharedWith: 'Also on',
  andMore: (n: number) => `and ${n} more`,
  reasonLabel: {
    no_longer_in_plan: 'No longer in plan',
    incapacity: 'Incapacity',
    hospice: 'Hospice',
    death: 'Death',
    other: 'Other',
  } as Record<string, string>,
  reasonPick: 'Reason',
  reasonPlaceholder: 'Pick a reason...',
  reasonRequired: 'Pick a reason first.',
  otherPlaceholder: 'Say why (kept in the audit only)',
  otherRequired: 'Other needs a few words saying why. They are kept in the audit only.',
  wordsTooLong: (max: number) => `Keep the words to ${max} characters.`,
  button: 'Add to technical exclusion',
  retryButton: 'Retry adding',
  cancel: 'Choose someone else',
  /* ---- the confirm: what this press writes, one line per row, and the tick when a phone is shared ---- */
  confirmHeading: (name: string, memberId: string, year: string) =>
    `Add ${name} (member ID ${memberId}, born ${year || 'year not on file'}) to technical exclusion?`,
  confirmReason: (reason: string) => `Reason: ${reason}.`,
  confirmWrites: 'This press writes:',
  /** The member row, before the press. */
  memberRowLine: (name: string) =>
    `Member hold: ${name} is held by member ID. Letters, kits and calls stop, and they are out of every campaign wave.`,
  /** A phone row, before the press. */
  phoneRowLine: (phone: string) => `Phone ${phone}: blocked. Every text to it stops.`,
  /** The other members on a shared phone, on the row's own line, before and after the press. */
  alsoBlocks: (who: string) => `Also blocks: ${who}.`,
  /** The tick. Required when any phone is shared; the press is refused without it. */
  sharedAck: (who: string) => `I understand the shared phone(s) are blocked too, so texts to ${who} stop as well.`,
  sharedAckRequired: 'Tick that you understand the shared phone(s) are blocked too, then send.',
  confirmLocked: 'The entries are locked; removing them later needs an admin and a reason.',
  confirmButton: 'Confirm and add',
  confirmBack: 'Back',
  /* ---- after the press: the same rows, each with its outcome ---- */
  outcomeHeading: (name: string) => `What was written for ${name}:`,
  memberRowOutcome: (status: 'done' | 'held' | 'refused', reason: string) =>
    status === 'done' ? 'Member hold: enforced.'
      : status === 'held' ? 'Member hold: already on the list, enforced.'
        : `Member hold: refused (${reason}).`,
  phoneRowOutcome: (phone: string, status: 'done' | 'held' | 'refused', reason: string) =>
    status === 'done' ? `Phone ${phone}: blocked.`
      : status === 'held' ? `Phone ${phone}: already on the list, blocked.`
        : `Phone ${phone}: refused (${reason}).`,
  added: (name: string) =>
    `${name} is on the technical exclusion list, locked. Every channel stops for them; the mark reaches their member file when the file is next updated.`,
  partial: (name: string, refused: string) =>
    `${name} is on the technical exclusion list, but part of it was refused: ${refused}. The rest stands and is locked.`,
  refused: (name: string, why: string) =>
    // CARE-07 review R3: `why` is fixed words that already name the platform, so it is said once.
    `${name} was not added: ${why.trim().replace(/\.+$/, '')}. Nothing was changed.`,
  refusedOutright: (why: string) =>
    `The platform refused this outright and wrote nothing (${why}). Clicking again will be refused the same way.`,
  unknown: (why: string) =>
    `The platform did not answer, so it is not known whether the member was added (${why}). Click again to retry safely: the same request is replayed and nothing is written twice.`,
  fileNotWritten: (why: string) =>
    ` The member's file did not get the mark (${why}); the list entries stand.`,
  platformOff: 'Adding to technical exclusion cannot be sent from this screen yet. Nothing was changed.',
  notEnabled: 'Adding to technical exclusion is not turned on yet.',
  notOnRoster: 'That member is not on this client\'s roster.',
  rosterUnreadable: 'The member list could not be read, so the member cannot be shown. Try again.',
  previewFailed: (why: string) => `The member could not be shown (${why}).`,
} as const;

/**
 * The current-campaign search (William, 23 Sep 2026: "matches now with the member directly for the
 * current campaign"): the campaign a manual pick sets in the same press. Only for an entry that names
 * no campaign (an entry's own campaign is what the server reads), and only a candidate on EXACTLY ONE
 * listed roster; a member on two rosters is matched and the campaign left for the operator, because
 * the console does not read launch dates (his row 228 rule is the arrival agent's to apply).
 */
export function pickCampaign(entryCampaign: string | null, c: { campaigns?: string[] }): string | null {
  if (entryCampaign) return null;
  return c.campaigns?.length === 1 ? c.campaigns[0] ?? null : null;
}
